import React, { useEffect, useMemo, useRef, useState } from "react";
import "../StockInward.css";
import "./Billing.css";
import { createPortal } from "react-dom";
import PortalDropdown from "../components/PortalDropdown";
import {
  addCustomer,
  addItem,
  addSalesPayment,
  addSalesEntry,
  createBillActionRequest,
  deleteSalesPayment,
  deleteSalesEntry,
  getCustomerByPhone,
  getStore,
  getCustomers,
  getItems,
  getMyBillActionRequests,
  getSalesEntries,
  updateSalesEntry,
  updateSalesEntryPaymentStatus,
  updateSalesEntryPaymentType,
  updateSalesPayment,
  getApiBaseRoot,
  saveReceiptHtml,
} from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";
import { getSettlementSummary, isPaymentSettlement } from "../utils/settlement";
import { confirmAndCreateQuickItem } from "../utils/quickItem";
import {
  buildLedgerPrintHtml,
  buildLedgerStatement,
  ledgerPartyName,
  localLedgerToday,
} from "./paymentLedgerModel";

const BILLS_PER_PAGE = 10;
const BILLING_CUSTOMER_MODAL_PAGE_SIZE = 10;
const DEFAULT_BILLING_ROWS = 5;
const BILL_PAYMENT_TYPES = ["Any", "UPI", "Cash", "Credit", "Cheque", "Acc Transfer", "Razorpay", "Card"];
const RECEIPT_CONFIG = {
  title: "ARF Seafoods",
  // Keep the QR implementation ready, but hide it until the approved code is available.
  showQrCode: false,
  headerLines: [
    "Verkodu",
    "Rameshwaram",
    "9894286738",
  ],
  footerLines: [
    "FOR A HEALTHY LIFE",
    "HAVE MORE FISH",
    "THANK YOU !!! VISIT AGAIN !!!",
  ],
  footerBrand: "ARF Seafoods",
};

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatQty(value) {
  const num = Number(value) || 0;
  return num.toFixed(2);
}

function formatAmount(value) {
  const num = Number(value) || 0;
  return num.toFixed(2);
}

function normalizeDiscountValue(value, grossAmount = Infinity) {
  const raw = Number(value);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  const safeGross = Number.isFinite(Number(grossAmount)) ? Math.max(0, Number(grossAmount) || 0) : Infinity;
  return +Math.min(raw, safeGross).toFixed(2);
}

function calcGrossAmount(items) {
  return +((items || []).reduce((sum, item) => sum + (Number(item?.amount) || 0), 0)).toFixed(2);
}

function formatDate(value) {
  return formatDisplayDate(value);
}

function formatBillNo(id) {
  if (id) {
    return String(id).slice(-4).toUpperCase();
  }
  return Date.now().toString().slice(-4);
}

function splitCustomerTextLines(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildCustomerLinesFromFields({ name, address, address2, phone }) {
  const lines = [];
  const nameText = String(name || "").trim();
  if (nameText) lines.push(nameText);
  lines.push(...splitCustomerTextLines(address));
  lines.push(...splitCustomerTextLines(address2));
  const phoneText = String(phone || "").trim();
  if (phoneText) lines.push(phoneText);
  return lines;
}

function getEntryCustomerDetails(entry) {
  const customerLines = splitCustomerTextLines(entry?.customer);
  const fallbackPhone = customerLines.length ? customerLines[customerLines.length - 1].replace(/\D/g, "") : "";
  return {
    phone: String(entry?.customerPhone || fallbackPhone || "").trim(),
    name: String(entry?.customerName || customerLines[0] || "").trim(),
    address: String(
      entry?.customerAddress ||
        (customerLines.length > 1 ? customerLines.slice(1, fallbackPhone ? -1 : undefined).join("\n") : "")
    ).trim(),
    address2: String(entry?.customerAddress2 || "").trim(),
  };
}

function buildCustomerLinesFromEntry(entry) {
  const customerDetails = getEntryCustomerDetails(entry);
  const customerLines = buildCustomerLinesFromFields(customerDetails);
  return customerLines.length ? customerLines : splitCustomerTextLines(entry?.customer);
}

function normalizeBillPaymentType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "any") return "Any";
  if (raw === "upi") return "UPI";
  if (raw === "cash") return "Cash";
  if (raw === "cheque" || raw === "check") return "Cheque";
  if (raw === "acc transfer" || raw === "account transfer" || raw === "bank transfer") return "Acc Transfer";
  if (raw === "razorpay") return "Razorpay";
  if (raw === "card" || raw === "credit card" || raw === "debit card") return "Card";
  if (raw === "credit") return "Credit";
  return "Any";
}

function billSortValue(entry) {
  const billNo = String(entry?.billNo || formatBillNo(entry?.id) || "");
  const digitPart = billNo.replace(/\D/g, "");
  if (digitPart) return Number(digitPart);
  const direct = Number(billNo);
  return Number.isFinite(direct) ? direct : 0;
}

async function getLogoDataUrl() {
  if (getLogoDataUrl.cache) return getLogoDataUrl.cache;
  const url = resolvePublicAsset("Logo.png");
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    getLogoDataUrl.cache = dataUrl;
    return dataUrl;
  } catch (e) {
    console.error("Logo load failed, using url", e);
    getLogoDataUrl.cache = url;
    return url;
  }
}

async function getQrDataUrl() {
  if (getQrDataUrl.cache) return getQrDataUrl.cache;
  const url = resolvePublicAsset("alifqr.PNG");
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    getQrDataUrl.cache = dataUrl;
    return dataUrl;
  } catch (e) {
    console.error("QR load failed, using url", e);
    getQrDataUrl.cache = url;
    return url;
  }
}

function resolvePublicAsset(path) {
  const cleanPath = String(path || "").replace(/^\/*/, "");
  const base = process.env.PUBLIC_URL || "";
  const basePath = String(base).replace(/\/+$/, "");
  const relative = `${basePath}/${cleanPath}`.replace(/\/{2,}/g, "/");
  try {
    return new URL(relative, window.location.href).toString();
  } catch (e) {
    return relative;
  }
}

function normalizePhoneCandidate(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  // Common Indian mobile formats.
  if (/^[6-9]\d{9}$/.test(digits)) return `91${digits}`;
  if (/^0[6-9]\d{9}$/.test(digits)) return `91${digits.slice(1)}`;
  if (/^91[6-9]\d{9}$/.test(digits)) return digits;
  if (/^091[6-9]\d{9}$/.test(digits)) return digits.slice(1);
  if (/^0091[6-9]\d{9}$/.test(digits)) return digits.slice(2);
  // Fallback for non-Indian numbers already carrying country code.
  if (digits.length >= 10 && digits.length <= 15) {
    return digits.startsWith("00") ? digits.slice(2) : digits;
  }
  return "";
}

function extractPhone(customerText) {
  const text = String(customerText || "");
  if (!text.trim()) return "";

  const tokenPattern = /(?:\+?\d[\d\s().-]{7,}\d)/g;
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const lineMatches = lines[i].match(tokenPattern) || [];
    for (let j = lineMatches.length - 1; j >= 0; j -= 1) {
      const normalized = normalizePhoneCandidate(lineMatches[j]);
      if (normalized) return normalized;
    }
  }

  const matches = text.match(tokenPattern) || [];
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const normalized = normalizePhoneCandidate(matches[i]);
    if (normalized) return normalized;
  }

  return "";
}

function extractCustomerPrimaryName(customerText) {
  const lines = String(customerText || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return "";
  const first = lines[0];
  // If the first line looks like a phone line, don't treat it as name.
  if (normalizePhoneCandidate(first)) return "";
  return first;
}

function samePhoneMatch(a, b) {
  const phoneA = normalizePhoneCandidate(a);
  const phoneB = normalizePhoneCandidate(b);
  if (phoneA && phoneB) {
    if (phoneA === phoneB) return true;
    const lastA = phoneA.replace(/\D/g, "").slice(-10);
    const lastB = phoneB.replace(/\D/g, "").slice(-10);
    if (lastA && lastB && lastA === lastB) return true;
  }
  const digitsA = String(a || "").replace(/\D/g, "");
  const digitsB = String(b || "").replace(/\D/g, "");
  if (digitsA.length >= 10 && digitsB.length >= 10) {
    return digitsA.slice(-10) === digitsB.slice(-10);
  }
  return false;
}

function recentCustomerFilterMeta(customerText) {
  const rawCustomer = String(customerText || "");
  const name = extractCustomerPrimaryName(rawCustomer).trim();
  const phone = normalizePhoneCandidate(extractPhone(rawCustomer));
  const fallback = rawCustomer
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  const displayName = name || (fallback && !normalizePhoneCandidate(fallback) ? fallback : "") || "Walk-in";
  const label = [displayName, phone].filter(Boolean).join(" - ") || "Walk-in";
  const search = [rawCustomer, displayName, phone, label].filter(Boolean).join(" ").toLowerCase();
  return { displayName, phone, label, search };
}

function buildWhatsAppText({
  billNo,
  billDate,
  customerLines,
  transporterName,
  numberOfBoxes,
  items,
  totalQty,
  totalAmount,
  discountAmount,
  receiptLink,
}) {
  const header = `*ARF Seafoods*%0A*Bill No:* ${encodeURIComponent(billNo)}%0A*Date:* ${encodeURIComponent(
    billDate
  )}`;
  const customer = customerLines.length ? `%0A*Customer:* ${encodeURIComponent(customerLines.join(", "))}` : "";
  const itemLines = items
    .slice(0, 15)
    .map((it, idx) =>
      encodeURIComponent(
        `${idx + 1}. ${it.material}${it.boxNumber ? ` | Box No: ${it.boxNumber}` : ""} | Qty: ${formatQty(
          it.qty
        )} | Rate: ${formatAmount(it.rate)} | Amt: ${formatAmount(it.amount)}`
      )
    )
    .join("%0A");
  const logisticsLines = [
    transporterName ? `%0A*Transporter:* ${encodeURIComponent(transporterName)}` : "",
    numberOfBoxes !== null && numberOfBoxes !== undefined && numberOfBoxes !== ""
      ? `%0A*Total Boxes:* ${encodeURIComponent(numberOfBoxes)}`
      : "",
  ].join("");
  const discountLine =
    Number(discountAmount || 0) > 0 ? `%0A*Discount:* ${formatAmount(discountAmount)}` : "";
  const totals = `%0A*Total Qty:* ${formatQty(totalQty)}${discountLine}%0A*Total Amt:* ${formatAmount(totalAmount)}`;
  const linkText = receiptLink ? `%0A%0AView bill: ${receiptLink}` : "";
  return `${header}${customer}${logisticsLines}%0A%0A${itemLines}${itemLines ? "%0A" : ""}${totals}${linkText}`;
}

function encodeHtmlToBase64(html) {
  try {
    return btoa(unescape(encodeURIComponent(html)));
  } catch (e) {
    console.error("Failed to encode receipt", e);
    return "";
  }
}

const INVOICE_ONES = [
  "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
];
const INVOICE_TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function invoiceIntegerToWords(value) {
  const number = Math.max(0, Math.floor(Number(value) || 0));
  if (number < 20) return INVOICE_ONES[number];
  if (number < 100) {
    return `${INVOICE_TENS[Math.floor(number / 10)]}${number % 10 ? ` ${INVOICE_ONES[number % 10]}` : ""}`;
  }
  if (number < 1000) {
    return `${INVOICE_ONES[Math.floor(number / 100)]} Hundred${number % 100 ? ` ${invoiceIntegerToWords(number % 100)}` : ""}`;
  }
  if (number < 100000) {
    return `${invoiceIntegerToWords(Math.floor(number / 1000))} Thousand${number % 1000 ? ` ${invoiceIntegerToWords(number % 1000)}` : ""}`;
  }
  if (number < 10000000) {
    return `${invoiceIntegerToWords(Math.floor(number / 100000))} Lakh${number % 100000 ? ` ${invoiceIntegerToWords(number % 100000)}` : ""}`;
  }
  return `${invoiceIntegerToWords(Math.floor(number / 10000000))} Crore${number % 10000000 ? ` ${invoiceIntegerToWords(number % 10000000)}` : ""}`;
}

function invoiceAmountInWords(value) {
  const amount = Math.max(0, Number(value) || 0);
  let rupees = Math.floor(amount);
  let paise = Math.round((amount - rupees) * 100);
  if (paise === 100) {
    rupees += 1;
    paise = 0;
  }
  return `Rupees ${invoiceIntegerToWords(rupees)}${paise ? ` and ${invoiceIntegerToWords(paise)} Paise` : ""} Only`;
}

export function buildReceiptHtml({
  logoUrl,
  qrUrl,
  billNo,
  billDate,
  billTime,
  customerLines,
  items,
  totalQty,
  discountAmount,
  totalAmount,
  balanceAmount,
  balanceLabel = "Balance Amount",
  storeName = RECEIPT_CONFIG.title,
  storeGst = "",
  customerGst = "",
  transporterName = "",
  numberOfBoxes = null,
  paymentMode = "",
  detailTitle = "Customer Details:",
  detailFallback = "Walk-in",
  documentLabel = "Bill No:",
  documentTitle,
}) {
  const resolvedQrUrl = RECEIPT_CONFIG.showQrCode
    ? qrUrl ||
      (() => {
        try {
          return resolvePublicAsset("alifqr.PNG");
        } catch (e) {
          return "";
        }
      })()
    : "";

  const customerBlock = customerLines.length
    ? customerLines.map((line) => `<div>${escapeHtml(line)}</div>`).join("")
    : `<div>${escapeHtml(detailFallback)}</div>`;

  const hasBoxNumbers = items.some((item) => String(item?.boxNumber || "").trim());
  const rows = items
    .map(
      (item, index) => `
        <tr class="item-row">
          <td class="center">${index + 1}</td>
          <td>${escapeHtml(item.material)}</td>
          ${hasBoxNumbers ? `<td class="center">${escapeHtml(item.boxNumber || "-")}</td>` : ""}
          <td class="right">${formatQty(item.qty)}</td>
          <td class="right">${formatAmount(item.rate)}</td>
          <td class="right">${formatAmount(item.amount)}</td>
        </tr>
      `
    )
    .join("");

  const grossAmount = calcGrossAmount(items);
  const resolvedDiscountAmount = normalizeDiscountValue(discountAmount, grossAmount);
  const hasBalanceAmount =
    balanceAmount !== undefined && balanceAmount !== null && Number.isFinite(Number(balanceAmount));
  const resolvedBalanceAmount = hasBalanceAmount ? Math.max(0, Number(balanceAmount) || 0) : null;
  const resolvedPaidAmount = hasBalanceAmount
    ? Math.max(0, Number(totalAmount || 0) - resolvedBalanceAmount)
    : null;
  const isPurchaseInvoice = /supplier|purchase/i.test(`${detailTitle} ${documentLabel} ${documentTitle || ""}`);
  const invoiceTitle = isPurchaseInvoice ? "PURCHASE INVOICE" : "TAX INVOICE";
  const invoiceDetails = [
    [String(documentLabel || "Invoice No").replace(/:\s*$/, ""), billNo],
    ["Date", billDate],
    ["Time", billTime],
    ["Transporter", transporterName],
    ["Total Boxes", numberOfBoxes],
    ["Payment Mode", paymentMode],
  ]
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
    .map(
      ([label, value]) => `
        <div class="invoice-detail-row">
          <span class="label">${escapeHtml(label)}</span><b>:</b><strong>${escapeHtml(value)}</strong>
        </div>`
    )
    .join("");

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      <title>${escapeHtml(documentTitle || `${invoiceTitle} ${billNo}`)}</title>
      <style>
        @page { size: A4; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, Helvetica, sans-serif;
          color: #000;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
        }
        .receipt {
          width: 194mm;
          min-height: 281mm;
          margin: 0 auto;
          border: 1.4px solid #111827;
          background: #fff;
          display: flex;
          flex-direction: column;
        }
        .center { text-align: center; }
        .right { text-align: right; }
        .bold { font-weight: 700; }
        .invoice-header { padding: 10px 14px 12px; border-bottom: 1px solid #111827; }
        .invoice-title-row { position: relative; min-height: 27px; display: flex; justify-content: center; align-items: flex-start; }
        .invoice-title { padding: 0 16px 4px; border-bottom: 1px solid #111827; font-size: 19px; font-weight: 800; letter-spacing: .04em; }
        .copy-label { position: absolute; top: 0; right: 0; font-size: 10px; font-weight: 800; }
        .company-block { display: flex; justify-content: center; align-items: center; gap: 14px; text-align: center; }
        .logo-wrap { width: 64px; flex: 0 0 64px; }
        .logo { width: 100%; max-height: 64px; object-fit: contain; display: block; }
        .company-name { font-size: 24px; line-height: 1.1; font-weight: 900; }
        .company-meta { margin-top: 3px; font-size: 12px; line-height: 1.4; font-weight: 700; }
        .party-invoice-grid { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #111827; }
        .party-panel, .invoice-panel { min-height: 142px; padding: 12px 14px; }
        .party-panel { border-right: 1px solid #111827; }
        .section-title {
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 800;
        }
        .customer-block { font-size: 13px; line-height: 1.55; font-weight: 700; }
        .gst-line { margin-top: 8px; }
        .invoice-details { display: grid; gap: 6px; }
        .invoice-detail-row { display: grid; grid-template-columns: 105px 10px minmax(0, 1fr); gap: 4px; align-items: start; font-size: 12px; font-weight: 700; }
        .label { font-weight: 800; }
        .items-table {
          width: 100%;
          height: 1px;
          min-height: 0;
          flex: 1 0 auto;
          border-collapse: collapse;
          table-layout: fixed;
        }
        .items-table th, .items-table td { padding: 7px 6px; border-right: 1px solid #111827; border-bottom: 1px solid #111827; vertical-align: top; }
        .items-table th:last-child, .items-table td:last-child { border-right: 0; }
        .items-table th { font-size: 11px; font-weight: 900; background: #f8fafc; }
        .items-table td { font-size: 12px; font-weight: 600; }
        .item-row td { height: 31px; }
        .items-spacer td { height: 100%; min-height: 24px; }
        .totals-row td { font-size: 13px; font-weight: 900; }
        .grand-total-label { text-align: right; }
        .invoice-summary {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(300px, 340px);
          grid-template-areas: "amount-words totals";
          border-bottom: 1px solid #111827;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .amount-words {
          grid-area: amount-words;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 10px 14px;
          border-right: 1px solid #111827;
          font-size: 13px;
          font-weight: 800;
        }
        .payment-summary { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 8px; font-size: 11px; font-weight: 700; }
        .totals-panel {
          grid-area: totals;
          width: 100%;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .summary-line {
          min-height: 30px;
          flex: 1 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
          padding: 7px 12px;
          border-bottom: 1px solid #111827;
          font-size: 12px;
          font-weight: 700;
        }
        .summary-line:last-child { border-bottom: 0; }
        .summary-line.discount { font-weight: 700; }
        .summary-line.net {
          font-weight: 900;
          font-size: 15px;
        }
        .summary-line.balance {
          font-weight: 900;
          font-size: 14px;
        }
        .invoice-footer {
          min-height: 150px;
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: 1.35fr 1fr;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .terms { padding: 10px 14px; border-right: 1px solid #111827; }
        .terms-title { padding-bottom: 7px; border-bottom: 1px solid #111827; font-size: 13px; font-weight: 900; }
        .terms ol { margin: 10px 0 0; padding-left: 18px; font-size: 11px; line-height: 1.7; font-weight: 700; }
        .signature { padding: 10px 14px; display: flex; flex-direction: column; justify-content: space-between; text-align: right; font-size: 12px; font-weight: 800; }
        .authorised { text-align: center; font-size: 11px; font-weight: 800; }
        .qr-block {
          padding: 8px 14px;
          border-top: 1px solid #111827;
          text-align: center;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .qr-image {
          width: 100%;
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto;
        }
        @media screen {
          body {
            padding: 18px;
            min-height: 100vh;
            min-height: 100dvh;
            background: #e5e7eb;
          }
          .receipt {
            width: 100%;
            max-width: 900px;
            min-height: calc(100vh - 36px);
            min-height: calc(100dvh - 36px);
          }
        }
        @media screen and (max-width: 640px) {
          body { padding: 0; background: #fff; }
          .receipt {
            max-width: none;
            min-height: 100vh;
            min-height: 100dvh;
            margin: 0;
            border: 0;
            display: block;
          }
          .invoice-header { padding: 9px 8px 10px; }
          .invoice-title { font-size: 15px; }
          .copy-label { font-size: 8px; }
          .company-block { gap: 8px; }
          .logo-wrap { width: 48px; flex-basis: 48px; }
          .company-name { font-size: 19px; }
          .company-meta { font-size: 10px; }
          .party-invoice-grid { grid-template-columns: 1fr; }
          .party-panel { min-height: 0; border-right: 0; border-bottom: 1px solid #111827; }
          .invoice-panel { min-height: 0; }
          .items-table { font-size: 10px; }
          .items-table th, .items-table td { padding: 5px 3px; overflow-wrap: anywhere; }
          .items-spacer { display: none; }
          .invoice-summary { display: block; }
          .amount-words { border-right: 0; border-top: 1px solid #111827; }
          .totals-panel { width: 100%; }
          .invoice-footer { grid-template-columns: 1fr; }
          .terms { border-right: 0; border-bottom: 1px solid #111827; }
          .signature { min-height: 115px; }
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        <header class="invoice-header">
          <div class="invoice-title-row">
            <div class="invoice-title">${invoiceTitle}</div>
            <div class="copy-label">Original For Recipient</div>
          </div>
          <div class="company-block">
            ${logoUrl ? `<div class="logo-wrap"><img class="logo" src="${escapeHtml(logoUrl)}" alt="Logo" /></div>` : ""}
            <div>
              <div class="company-name">${escapeHtml(storeName)}</div>
              <div class="company-meta">
                ${customerGst ? `<div>GSTIN: ${escapeHtml(storeGst || "Not provided")}</div>` : ""}
                <div>${RECEIPT_CONFIG.headerLines.map((line) => escapeHtml(line)).join(" | ")}</div>
              </div>
            </div>
          </div>
        </header>
        <section class="party-invoice-grid">
          <div class="party-panel">
            <div class="section-title">${escapeHtml(detailTitle)}</div>
            <div class="customer-block">${customerBlock}${customerGst ? `<div class="gst-line"><span class="label">GSTIN/UIN:</span> ${escapeHtml(customerGst)}</div>` : ""}</div>
          </div>
          <div class="invoice-panel"><div class="invoice-details">${invoiceDetails}</div></div>
        </section>
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 7%;" class="center">S.N.</th>
              <th style="width: ${hasBoxNumbers ? "37" : "49"}%;">Description of Goods</th>
              ${hasBoxNumbers ? '<th style="width: 12%;" class="center">Box No.</th>' : ""}
              <th style="width: 12%;" class="right">Qty.</th>
              <th style="width: 15%;" class="right">Price</th>
              <th style="width: 17%;" class="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
            <tr class="items-spacer" aria-hidden="true">
              <td></td>
              <td></td>
              ${hasBoxNumbers ? "<td></td>" : ""}
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr class="totals-row">
              <td colspan="${hasBoxNumbers ? 3 : 2}" class="grand-total-label">Grand Total:</td>
              <td class="right">${formatQty(totalQty)}</td>
              <td></td>
              <td class="right">${formatAmount(grossAmount)}</td>
            </tr>
          </tbody>
        </table>
        <section class="invoice-summary">
          <div class="totals-panel">
            <div class="summary-line"><span>Gross Amount</span><span>${formatAmount(grossAmount)}</span></div>
            ${
              resolvedDiscountAmount > 0
                ? `<div class="summary-line discount"><span>Discount</span><span>- ${formatAmount(resolvedDiscountAmount)}</span></div>`
                : ""
            }
            <div class="summary-line net"><span>Total Amount</span><span>${formatAmount(totalAmount)}</span></div>
            ${
              hasBalanceAmount
                ? `<div class="summary-line balance"><span>${escapeHtml(balanceLabel)}</span><span>${formatAmount(resolvedBalanceAmount)}</span></div>`
                : ""
            }
          </div>
          <div class="amount-words">
            <strong>${escapeHtml(invoiceAmountInWords(totalAmount))}</strong>
            <div class="payment-summary">
              ${paymentMode ? `<span>Payment Mode: <b>${escapeHtml(paymentMode)}</b></span>` : ""}
              ${hasBalanceAmount ? `<span>Paid: <b>${formatAmount(resolvedPaidAmount)}</b></span><span>Balance: <b>${formatAmount(resolvedBalanceAmount)}</b></span>` : ""}
            </div>
          </div>
        </section>
        <footer class="invoice-footer">
          <div class="terms">
            <div class="terms-title">Terms &amp; Conditions</div>
            <ol>
              <li>Please verify the quantity and amount before acceptance.</li>
              <li>Subject to local jurisdiction only.</li>
              <li>This is a computer-generated invoice.</li>
            </ol>
          </div>
          <div class="signature">
            <div>For ${escapeHtml(storeName)}</div>
            <div class="authorised">Authorised Signature</div>
          </div>
        </footer>
        ${
          resolvedQrUrl
            ? `<div class="qr-block"><img class="qr-image" src="${escapeHtml(resolvedQrUrl)}" alt="QR Code" /></div>`
            : ""
        }
      </div>
    </body>
  </html>`;
}

function makeLineFromItem(item) {
  const qty = 1;
  const rate = Number(item.sellingPrice) || 0;
  return {
    itemId: item.id,
    code: item.code || "",
    itemGroup: item.itemGroup || "",
    name: item.name || "",
    boxNumber: "",
    qty,
    rate,
    amount: +(qty * rate).toFixed(2),
  };
}

function makeEmptyLine() {
  return { itemId: null, code: "", itemGroup: "", name: "", boxNumber: "", qty: "", rate: "", amount: 0 };
}

function makeDefaultBillingLines() {
  return Array.from({ length: DEFAULT_BILLING_ROWS }, () => makeEmptyLine());
}

function padBillingLines(lines) {
  const next = Array.isArray(lines) ? [...lines] : [];
  while (next.length < DEFAULT_BILLING_ROWS) {
    next.push(makeEmptyLine());
  }
  return next;
}

function makeCustomerPaidForm() {
  return {
    date: new Date().toISOString().slice(0, 10),
    amount: "",
  };
}

function makeNewCustomerForm(phone = "") {
  return {
    name: "",
    phone,
    address: "",
    address2: "",
  };
}

export default function Billing({ currentUser, category = "Billing" }) {
  const BILLING_CATEGORY = category;
  const billingTitle = category === "Stall" ? "Stall Billing" : "Sales";
  const role = (currentUser?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const canUpdatePaymentStatus = role === "admin" || role === "executive";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  const [itemSuggestionIndex, setItemSuggestionIndex] = useState(-1);
  const [billDate, setBillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerAddress2, setCustomerAddress2] = useState("");
  const [transporterName, setTransporterName] = useState("");
  const [numberOfBoxes, setNumberOfBoxes] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerSuggestionIndex, setCustomerSuggestionIndex] = useState(-1);
  const [customerSuggestionAnchor, setCustomerSuggestionAnchor] = useState("phone");
  const [customerLookup, setCustomerLookup] = useState({ status: "", message: "" });
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState(() => makeNewCustomerForm());
  const [newCustomerError, setNewCustomerError] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [lines, setLines] = useState(() => makeDefaultBillingLines());
  const [entries, setEntries] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [paymentUpdating, setPaymentUpdating] = useState({});
  const [paymentTypeUpdating, setPaymentTypeUpdating] = useState({});
  const [billDeleting, setBillDeleting] = useState({});
  const [billRequesting, setBillRequesting] = useState({});
  const [myBillRequests, setMyBillRequests] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [customerFilter, setCustomerFilter] = useState("");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const whatsappBusy = useRef(false);
  const [showRecentCustomerSuggestions, setShowRecentCustomerSuggestions] = useState(false);
  const [recentCustomerSuggestionIndex, setRecentCustomerSuggestionIndex] = useState(-1);
  const [customerDetailsModal, setCustomerDetailsModal] = useState({ open: false, metric: "amount" });
  const [customerDetailsPage, setCustomerDetailsPage] = useState(1);
  const [customerPaidForm, setCustomerPaidForm] = useState(() => makeCustomerPaidForm());
  const [customerPaidSaving, setCustomerPaidSaving] = useState(false);
  const [customerPaidEdit, setCustomerPaidEdit] = useState({ paymentId: "", date: "", amount: "", mode: "Cash" });
  const [customerPaidUpdating, setCustomerPaidUpdating] = useState(false);
  const [customerPaidDeleting, setCustomerPaidDeleting] = useState({});
  const [markDonePaymentModal, setMarkDonePaymentModal] = useState({
    open: false,
    entryId: "",
    billNo: "",
    paymentType: "Any",
    pendingAmount: 0,
  });
  const [markDonePaymentForm, setMarkDonePaymentForm] = useState(() => ({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
  }));
  const [markDonePaymentSaving, setMarkDonePaymentSaving] = useState(false);
  const [billsPage, setBillsPage] = useState(1);
  const [activePanel, setActivePanel] = useState("billing");
  const [pendingFocus, setPendingFocus] = useState(null);
  const [billSaving, setBillSaving] = useState(false);
  const [openMaterialLineIndex, setOpenMaterialLineIndex] = useState(null);
  const [activeMaterialLineSuggestionIndex, setActiveMaterialLineSuggestionIndex] = useState(-1);
  const [materialLineDropdownRect, setMaterialLineDropdownRect] = useState(null);
  const qtyRefs = useRef({});
  const rateRefs = useRef({});
  const boxNumberRefs = useRef({});
  const materialLineRefs = useRef({});
  const materialLineSuggestionRefs = useRef([]);
  const searchRef = useRef(null);
  const skipLookupRef = useRef(false);
  const billSavingRef = useRef(false);
  const customerSuggestionRefs = useRef([]);
  const recentCustomerSuggestionRefs = useRef([]);
  const itemSuggestionRefs = useRef([]);
  const quickItemPendingRowsRef = useRef(new Set());

  const refreshItems = async () => {
    try {
      const list = await getItems();
      setItems(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load items.");
    }
  };

  const refreshBills = async () => {
    try {
      const list = await getSalesEntries();
      setEntries(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load bills.");
    }
  };

  const refreshCustomers = async () => {
    try {
      const list = await getCustomers();
      setCustomers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  const refreshMyRequests = async () => {
    if (isAdmin) {
      setMyBillRequests([]);
      return;
    }
    try {
      const list = await getMyBillActionRequests();
      setMyBillRequests(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  };

  const setPaymentDone = async (entryId, prevDone, nextDone) => {
    const prevValue = !!prevDone;
    const nextValue = !!nextDone;
    if (prevValue === nextValue) return true;

    setEntries((prev) =>
      prev.map((en) => (en.id === entryId ? { ...en, paymentDone: nextValue } : en))
    );
    setPaymentUpdating((prev) => ({ ...prev, [entryId]: true }));
    let success = true;
    try {
      await updateSalesEntryPaymentStatus(entryId, nextValue);
      setEditingBill((prev) => (prev && prev.id === entryId ? { ...prev, paymentDone: nextValue } : prev));
    } catch (err) {
      success = false;
      console.error(err);
      setEntries((prev) =>
        prev.map((en) => (en.id === entryId ? { ...en, paymentDone: prevValue } : en))
      );
      alert("Failed to update payment status.");
    } finally {
      setPaymentUpdating((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    }
    return success;
  };

  const entryPendingAmount = (entry) =>
    getSettlementSummary(Number(entry?.total || 0), entry?.payments).pendingAmount;

  const openMarkDonePaymentModal = (entry) => {
    if (!entry?.id) return;
    const pendingAmount = +entryPendingAmount(entry).toFixed(2);
    if (pendingAmount <= 0) {
      void setPaymentDone(entry.id, entry.paymentDone ?? false, true);
      return;
    }
    setMarkDonePaymentModal({
      open: true,
      entryId: entry.id,
      billNo: entry.billNo || formatBillNo(entry.id),
      paymentType: normalizeBillPaymentType(entry.paymentType),
      pendingAmount,
    });
    setMarkDonePaymentForm({
      date: new Date().toISOString().slice(0, 10),
      amount: pendingAmount.toFixed(2),
    });
  };

  const closeMarkDonePaymentModal = () => {
    if (markDonePaymentSaving) return;
    setMarkDonePaymentModal({
      open: false,
      entryId: "",
      billNo: "",
      paymentType: "Any",
      pendingAmount: 0,
    });
    setMarkDonePaymentForm({
      date: new Date().toISOString().slice(0, 10),
      amount: "",
    });
  };

  const submitMarkDonePaymentModal = async () => {
    const entryId = String(markDonePaymentModal.entryId || "").trim();
    if (!entryId) return;

    const amount = Number(markDonePaymentForm.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(markDonePaymentForm.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    const latestEntry = entries.find((en) => String(en?.id || "") === entryId);
    const pendingAmount = latestEntry
      ? +entryPendingAmount(latestEntry).toFixed(2)
      : Number(markDonePaymentModal.pendingAmount || 0);
    if (pendingAmount <= 0) {
      const changed = await setPaymentDone(entryId, false, true);
      if (changed) {
        await refreshBills();
      }
      closeMarkDonePaymentModal();
      return;
    }
    if (amount > pendingAmount + 0.0001) {
      alert(`Amount exceeds pending balance (${pendingAmount.toFixed(2)}).`);
      return;
    }

    const rawMode = normalizeBillPaymentType(
      latestEntry?.paymentType || markDonePaymentModal.paymentType || "Any"
    );
    const mode = rawMode === "Credit" ? "Cash" : rawMode;

    setMarkDonePaymentSaving(true);
    try {
      await addSalesPayment(entryId, {
        amount: +amount.toFixed(2),
        date,
        mode,
      });
      const changed = await setPaymentDone(entryId, false, true);
      if (!changed) {
        await refreshBills();
        alert("Payment was added, but payment status update failed.");
        return;
      }
      await refreshBills();
      closeMarkDonePaymentModal();
      alert("Payment added and status updated to Done.");
    } catch (err) {
      console.error(err);
      await refreshBills();
      alert(err?.message || "Failed to add payment.");
    } finally {
      setMarkDonePaymentSaving(false);
    }
  };

  const setPaymentType = async (entryId, prevType, prevDone, nextTypeRaw) => {
    const currentEntry = entries.find((en) => String(en?.id || "") === String(entryId || ""));
    const prevPaymentType = normalizeBillPaymentType(currentEntry?.paymentType ?? prevType ?? "Any");
    const nextPaymentType = normalizeBillPaymentType(nextTypeRaw);
    const prevPaymentDone =
      typeof currentEntry?.paymentDone === "boolean" ? currentEntry.paymentDone : !!prevDone;
    if (prevPaymentType === nextPaymentType) return;

    const forceNotDone = nextPaymentType === "Credit";

    setEntries((prev) =>
      prev.map((en) =>
        en.id === entryId
          ? {
              ...en,
              paymentType: nextPaymentType,
              ...(forceNotDone ? { paymentDone: false } : {}),
            }
          : en
      )
    );
    setPaymentTypeUpdating((prev) => ({ ...prev, [entryId]: true }));
    try {
      const res = await updateSalesEntryPaymentType(entryId, nextPaymentType);
      const savedPaymentType = normalizeBillPaymentType(res?.paymentType || nextPaymentType);
      const savedPaymentDone =
        typeof res?.paymentDone === "boolean" ? res.paymentDone : forceNotDone ? false : prevPaymentDone;

      setEntries((prev) =>
        prev.map((en) =>
          en.id === entryId ? { ...en, paymentType: savedPaymentType, paymentDone: savedPaymentDone } : en
        )
      );
      setEditingBill((prev) =>
        prev && prev.id === entryId
          ? { ...prev, paymentType: savedPaymentType, paymentDone: savedPaymentDone }
          : prev
      );
      await refreshBills();
    } catch (err) {
      console.error(err);
      setEntries((prev) =>
        prev.map((en) =>
          en.id === entryId
            ? { ...en, paymentType: prevPaymentType, paymentDone: prevPaymentDone }
            : en
        )
      );
      setEditingBill((prev) =>
        prev && prev.id === entryId
          ? { ...prev, paymentType: prevPaymentType, paymentDone: prevPaymentDone }
          : prev
      );
      alert(err?.message || "Failed to update payment type.");
    } finally {
      setPaymentTypeUpdating((prev) => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    }
  };

  const handleDeleteBill = async (entry) => {
    if (!entry?.id) return;
    const label = entry?.billNo || formatBillNo(entry?.id);
    if (!window.confirm(`Delete bill ${label}?`)) return;
    setBillDeleting((prev) => ({ ...prev, [entry.id]: true }));
    try {
      await deleteSalesEntry(entry.id);
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
      await refreshBills();
      await refreshMyRequests();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete bill.");
    } finally {
      setBillDeleting((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    }
  };

  useEffect(() => {
    refreshItems();
    refreshBills();
    refreshCustomers();
    refreshMyRequests();
  }, []);

  const requestByEntry = useMemo(() => {
    const map = {};
    for (const req of Array.isArray(myBillRequests) ? myBillRequests : []) {
      const entryId = req?.entryId;
      const action = req?.action;
      if (!entryId || !action) continue;
      if (!map[entryId]) map[entryId] = {};
      if (!map[entryId][action]) {
        map[entryId][action] = req;
      }
    }
    return map;
  }, [myBillRequests]);

  const requestKey = (entryId, action) => `${String(entryId)}:${String(action)}`;

  const requestBillAction = async (entry, action) => {
    if (!entry?.id) return;
    if (!window.confirm(`Send ${action} request to admin for this bill?`)) return;
    const key = requestKey(entry.id, action);
    setBillRequesting((prev) => ({ ...prev, [key]: true }));
    try {
      await createBillActionRequest(entry.id, action);
      await refreshMyRequests();
      alert("Request sent to admin.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to send request.");
    } finally {
      setBillRequesting((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const billingItems = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return billingItems;
    return billingItems.filter((item) => {
      const code = (item.code || "").toLowerCase();
      const name = (item.name || "").toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [billingItems, query]);

  useEffect(() => {
    const hasQuery = query.trim() !== "";
    setItemSuggestionIndex(hasQuery && filteredItems.length > 0 ? 0 : -1);
    if (!hasQuery) {
      setShowItemSuggestions(false);
    }
  }, [filteredItems, query]);

  useEffect(() => {
    if (itemSuggestionIndex < 0) return;
    const target = itemSuggestionRefs.current[itemSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [itemSuggestionIndex, filteredItems]);

  useEffect(() => {
    if (openMaterialLineIndex == null || activeMaterialLineSuggestionIndex < 0) return;
    materialLineSuggestionRefs.current[activeMaterialLineSuggestionIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeMaterialLineSuggestionIndex, openMaterialLineIndex]);

  const lookupCustomer = async (phone) => {
    const trimmed = String(phone || "").trim();
    if (!trimmed || trimmed.length < 10) {
      setCustomerLookup({ status: "", message: "" });
      setCustomerSuggestions([]);
      return;
    }
    setCustomerLookup({ status: "loading", message: "Searching..." });
    try {
      const data = await getCustomerByPhone(trimmed);
      setCustomerName(data?.name || "");
      setCustomerAddress(data?.address || "");
      setCustomerAddress2(data?.address2 || "");
      setCustomerLookup({ status: "found", message: "Customer found" });
      setCustomerSuggestions([]);
    } catch (err) {
      console.error(err);
      const notFound = err?.message === "Not found";
      if (notFound) {
        setCustomerName("");
        setCustomerAddress("");
        setCustomerAddress2("");
        if (!showCustomerModal) {
          setNewCustomer(makeNewCustomerForm(trimmed));
          setNewCustomerError("");
          setShowCustomerModal(true);
        }
      }
      const message = notFound ? "Customer not found" : "Failed to fetch customer";
      setCustomerLookup({ status: "notfound", message });
      setCustomerSuggestions([]);
    }
  };

  useEffect(() => {
    const phoneText = customerPhone.trim();
    const nameText = customerName.trim();
    const activeAnchor =
      customerSuggestionAnchor === "name"
        ? "name"
        : customerSuggestionAnchor === "phone"
          ? "phone"
          : nameText
            ? "name"
            : "phone";
    const activeQuery = activeAnchor === "name" ? nameText : phoneText;
    if (skipLookupRef.current) {
      skipLookupRef.current = false;
      setCustomerSuggestions([]);
      return undefined;
    }
    const query = activeQuery.toLowerCase();
    const matches = query
      ? customers
          .filter(
            (c) =>
              String(c.phone || "").toLowerCase().includes(query) ||
              String(c.name || "").toLowerCase().includes(query)
          )
          .slice(0, 8)
      : [];
    setCustomerSuggestions(matches);
    if (!phoneText || phoneText.length < 10) {
      setCustomerLookup({ status: "", message: "" });
      return undefined;
    }
    const timer = setTimeout(() => {
      lookupCustomer(phoneText);
    }, 400);
    return () => clearTimeout(timer);
  }, [customerPhone, customerName, customers, customerSuggestionAnchor]);

  useEffect(() => {
    setCustomerSuggestionIndex(customerSuggestions.length ? 0 : -1);
  }, [customerSuggestions]);

  useEffect(() => {
    if (customerSuggestionIndex < 0) return;
    const target = customerSuggestionRefs.current[customerSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [customerSuggestionIndex, customerSuggestions]);

  const totals = useMemo(() => {
    const totalQty = lines.reduce((s, line) => s + (Number(line.qty) || 0), 0);
    const grossAmount = lines.reduce((s, line) => s + (Number(line.amount) || 0), 0);
    const itemCount = lines.filter((line) => String(line?.name || line?.code || "").trim()).length;
    const appliedDiscountAmount = normalizeDiscountValue(discountAmount, grossAmount);
    const netAmount = Math.max(0, grossAmount - appliedDiscountAmount);
    return {
      totalQty,
      grossAmount: +grossAmount.toFixed(2),
      discountAmount: appliedDiscountAmount,
      totalAmount: +netAmount.toFixed(2),
      itemCount,
    };
  }, [lines, discountAmount]);

  const addItemToBill = (item, focusField = "") => {
    setLines((prev) => {
      const next = [...prev];
      const emptyIndex = next.findIndex((line) => !String(line?.name || line?.code || "").trim());
      if (emptyIndex >= 0) {
        next[emptyIndex] = makeLineFromItem(item);
        return next;
      }
      return [...next, makeLineFromItem(item)];
    });
    if (focusField) {
      setPendingFocus({ target: "last", field: focusField });
    }
  };

  const applyItemSuggestion = (item) => {
    if (!item) return;
    addItemToBill(item, "boxNumber");
    setQuery("");
    setShowItemSuggestions(false);
    setItemSuggestionIndex(-1);
    itemSuggestionRefs.current = [];
    if (searchRef.current && typeof searchRef.current.focus === "function") {
      searchRef.current.focus();
    }
  };

  useEffect(() => {
    if (!pendingFocus) return;
    const index = pendingFocus.target === "last" ? lines.length - 1 : -1;
    if (index < 0) return;
    const target =
      pendingFocus.field === "rate"
        ? rateRefs.current[index]
        : pendingFocus.field === "boxNumber"
          ? boxNumberRefs.current[index]
          : qtyRefs.current[index];
    if (target && typeof target.focus === "function") {
      target.focus();
      if (typeof target.select === "function") {
        target.select();
      }
      setPendingFocus(null);
    }
  }, [lines, pendingFocus]);

  const addFromSearch = () => {
    const q = query.trim().toLowerCase();
    if (!q) return;
    const exact = billingItems.find((item) => {
      const code = (item.code || "").toLowerCase();
      const name = (item.name || "").toLowerCase();
      return code === q || name === q;
    });
    const picked =
      exact ||
      filteredItems[
        itemSuggestionIndex >= 0 && itemSuggestionIndex < filteredItems.length ? itemSuggestionIndex : 0
      ];
    if (picked) {
      applyItemSuggestion(picked);
    }
  };

  const handleItemSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && query.trim() && filteredItems.length > 0) {
      e.preventDefault();
      setShowItemSuggestions(true);
      setItemSuggestionIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
      return;
    }
    if (e.key === "ArrowUp" && query.trim() && filteredItems.length > 0) {
      e.preventDefault();
      setShowItemSuggestions(true);
      setItemSuggestionIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (e.key === "Escape") {
      setShowItemSuggestions(false);
      setItemSuggestionIndex(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      addFromSearch();
    }
  };

  const receiptGstDetails = async (customerLines) => {
    const phone = extractPhone((customerLines || []).join("\n"));
    const [store, customerList] = await Promise.all([getStore(), phone ? getCustomers() : Promise.resolve([])]);
    const matches = customerList.filter((entry) => samePhoneMatch(entry.phone, phone));
    const customer = matches.length === 1 ? matches[0] : null;
    return { storeName: store.name || RECEIPT_CONFIG.title, storeGst: store.gstNumber || "", customerGst: customer?.gstNumber || "" };
  };

  const printReceipt = async (receiptData) => {
    const printWindow = window.open("", "PRINT", "height=600,width=400");
    if (!printWindow) {
      alert("Please allow popups to print the bill.");
      return;
    }

    const [resolvedLogoUrl, resolvedQrUrl] = await Promise.all([
      receiptData?.logoUrl
        ? Promise.resolve(receiptData.logoUrl)
        : getLogoDataUrl().catch(() => {
            try {
              return resolvePublicAsset("Logo.png");
            } catch (e) {
              return "";
            }
          }),
      RECEIPT_CONFIG.showQrCode
        ? receiptData?.qrUrl
          ? Promise.resolve(receiptData.qrUrl)
          : getQrDataUrl().catch(() => {
              try {
                return resolvePublicAsset("alifqr.PNG");
              } catch (e) {
                return "";
              }
            })
        : Promise.resolve(""),
    ]);

    let gstDetails;
    try { gstDetails = await receiptGstDetails(receiptData.customerLines); }
    catch (err) { printWindow.close(); alert("Could not load receipt GST details. Please retry printing. " + err.message); return; }
    const html = buildReceiptHtml({ ...receiptData, ...gstDetails, logoUrl: resolvedLogoUrl, qrUrl: resolvedQrUrl });

    const triggerPrint = () => {
      try {
        printWindow.focus();
      } catch (e) {
        // ignore
      }
      setTimeout(() => {
        try {
          printWindow.print();
        } finally {
          try {
            printWindow.close();
          } catch (e) {
            // ignore
          }
        }
      }, 50);
    };

    printWindow.document.write(html);
    printWindow.document.close();

    const waitForImages = () => {
      const images = Array.from(printWindow.document.images || []);
      if (images.length === 0) {
        return Promise.resolve();
      }
      return Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
        )
      );
    };

    Promise.race([waitForImages(), new Promise((resolve) => setTimeout(resolve, 2500))]).then(
      () => triggerPrint()
    );
  };

  const handlePrintEntry = async (entry) => {
    const customerLines = buildCustomerLinesFromEntry(entry);
    const logoUrl = await getLogoDataUrl();
    printReceipt({
      billNo: entry?.billNo || formatBillNo(entry?.id),
      billDate: formatDate(entry?.saleDate),
      billTime: "",
      customerLines,
      items: entry?.items || [],
      totalQty: entry?.totalQty || 0,
      discountAmount: entry?.discountAmount || 0,
      totalAmount: entry?.total || 0,
      balanceAmount: entryPendingAmount(entry),
      paymentMode: normalizeBillPaymentType(entry?.paymentType),
      transporterName: entry?.transporterName || "",
      numberOfBoxes: entry?.numberOfBoxes ?? null,
      logoUrl,
    });
  };

  const handleLedgerPrintEntry = async (entry) => {
    const partyName = ledgerPartyName("sales", entry);
    if (!partyName) {
      alert("Customer name is required to print the ledger.");
      return;
    }

    const printWindow = window.open("", "SALES_LEDGER_PRINT", "height=850,width=1100");
    if (!printWindow) {
      alert("Please allow popups to print the sales ledger.");
      return;
    }

    const statement = buildLedgerStatement({
      type: "sales",
      entries,
      partyName,
      asOfDate: localLedgerToday(),
    });
    const logoUrl = await getLogoDataUrl().catch(() => "");
    printWindow.document.write(buildLedgerPrintHtml(statement, logoUrl));
    printWindow.document.close();

    const images = Array.from(printWindow.document.images || []);
    const imageReady = Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            if (image.complete) {
              resolve();
              return;
            }
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
          })
      )
    );
    await Promise.race([imageReady, new Promise((resolve) => setTimeout(resolve, 2000))]);
    printWindow.focus();
    printWindow.print();
  };

  const addCustomLine = () => {
    setLines((prev) => [...prev, makeEmptyLine()]);
  };

  const openMaterialLineSuggestions = (index, value) => {
    const queryValue = String(value || "").trim();
    if (queryValue.length < 2) {
      setOpenMaterialLineIndex(null);
      setActiveMaterialLineSuggestionIndex(-1);
      setMaterialLineDropdownRect(null);
      return;
    }
    const anchor = materialLineRefs.current[index];
    const rect = anchor?.getBoundingClientRect?.();
    if (rect) {
      setMaterialLineDropdownRect({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpenMaterialLineIndex(index);
    setActiveMaterialLineSuggestionIndex(0);
  };

  const closeMaterialLineSuggestions = () => {
    setOpenMaterialLineIndex(null);
    setActiveMaterialLineSuggestionIndex(-1);
    setMaterialLineDropdownRect(null);
  };

  const selectMaterialForLine = (index, item) => {
    if (!item) return;
    setLines((prev) => {
      const next = [...prev];
      const target = { ...next[index] };
      const rate = Number(item?.sellingPrice) || 0;
      target.itemId = item?.id || null;
      target.code = item?.code || "";
      target.itemGroup = item?.itemGroup || "";
      target.name = item?.name || item?.code || "";
      target.rate = rate;
      target.amount = +((Number(target.qty) || 0) * rate).toFixed(2);
      next[index] = target;
      return next;
    });
    closeMaterialLineSuggestions();
    setTimeout(() => {
      const target = boxNumberRefs.current[index];
      target?.focus?.();
      target?.select?.();
    }, 0);
  };

  const updateLine = (index, field, value) => {
    setLines((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (field === "name") {
        updated.itemId = null;
        updated.code = "";
        updated.itemGroup = "";
        const normalizedValue = String(value || "").trim().toLowerCase();
        const matchedItem = billingItems.find(
          (item) =>
            String(item?.name || "").trim().toLowerCase() === normalizedValue ||
            String(item?.code || "").trim().toLowerCase() === normalizedValue
        );
        if (matchedItem) {
          updated.itemId = matchedItem.id;
          updated.code = matchedItem.code || "";
          updated.itemGroup = matchedItem.itemGroup || "";
          updated.name = matchedItem.name || matchedItem.code || value;
          updated.rate = Number(matchedItem.sellingPrice) || 0;
        }
      }
      const qty = Number(updated.qty) || 0;
      const rate = Number(updated.rate) || 0;
      updated.amount = +(qty * rate).toFixed(2);
      next[index] = updated;
      return next;
    });
  };

  const focusMaterialLine = (index) => {
    const input = materialLineRefs.current[index]?.querySelector?.("input");
    input?.focus?.();
    input?.select?.();
  };

  const ensureBillingItemForLine = async (index, inputValue) => {
    const itemName = String(inputValue || "").trim();
    if (!itemName || quickItemPendingRowsRef.current.has(index)) return;

    quickItemPendingRowsRef.current.add(index);
    try {
      const result = await confirmAndCreateQuickItem({
        name: itemName,
        itemType: "common",
        items: billingItems,
        confirmCreate: (message) => window.confirm(message),
        createItem: (payload) => addItem(payload),
      });
      if (result.item) {
        if (result.status === "created") {
          setItems((current) => {
            const savedId = String(result.item?.id || "");
            const alreadyLoaded = current.some(
              (item) =>
                (savedId && String(item?.id || "") === savedId) ||
                String(item?.name || "").trim().toLocaleLowerCase() === itemName.toLocaleLowerCase()
            );
            return alreadyLoaded ? current : [result.item, ...current];
          });
        }
        selectMaterialForLine(index, result.item);
        return;
      }
      if (result.status === "cancelled") {
        setTimeout(() => focusMaterialLine(index), 0);
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to add the new item to Item Master.");
      setTimeout(() => focusMaterialLine(index), 0);
    } finally {
      quickItemPendingRowsRef.current.delete(index);
    }
  };

  const removeLine = (index) => {
    setLines((prev) => padBillingLines(prev.filter((_, idx) => idx !== index)));
  };

  const clearBill = () => {
    closeMaterialLineSuggestions();
    setLines(makeDefaultBillingLines());
    setDiscountAmount("");
    setEditingBill(null);
    setBillDate(new Date().toISOString().slice(0, 10));
    setCustomerPhone("");
    setCustomerName("");
    setCustomerAddress("");
    setCustomerAddress2("");
    setTransporterName("");
    setNumberOfBoxes("");
    setCustomerLookup({ status: "", message: "" });
    setCustomerSuggestions([]);
    setCustomerSuggestionIndex(-1);
    setCustomerSuggestionAnchor("phone");
    setShowCustomerModal(false);
    setNewCustomer(makeNewCustomerForm());
    setNewCustomerError("");
  };

  const applyCustomerSuggestion = (cust) => {
    const phone = cust?.phone || "";
    const name = cust?.name || "";
    const address = cust?.address || "";
    const address2 = cust?.address2 || "";
    skipLookupRef.current = true;
    setCustomerPhone(phone);
    setCustomerName(name);
    setCustomerAddress(address);
    setCustomerAddress2(address2);
    setCustomerLookup({ status: "found", message: "Customer selected" });
    setCustomerSuggestions([]);
    setCustomerSuggestionIndex(-1);
    setCustomerSuggestionAnchor("phone");
  };

  const handleCustomerSuggestionKeyDown = (e) => {
    if (e.key === "ArrowDown" && customerSuggestions.length > 0) {
      e.preventDefault();
      setCustomerSuggestionIndex((prev) => Math.min(prev + 1, customerSuggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp" && customerSuggestions.length > 0) {
      e.preventDefault();
      setCustomerSuggestionIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (e.key === "Escape") {
      setCustomerSuggestions([]);
      setCustomerSuggestionIndex(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (customerSuggestions.length > 0) {
        applyCustomerSuggestion(
          customerSuggestions[
            customerSuggestionIndex >= 0 ? customerSuggestionIndex : 0
          ]
        );
        return;
      }
      lookupCustomer(customerPhone);
    }
  };

  const renderCustomerSuggestions = () =>
    customerSuggestions.length > 0 ? (
      <div
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "#fff",
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          marginTop: 4,
          zIndex: 20,
          maxHeight: 180,
          overflowY: "auto",
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        }}
      >
        {customerSuggestions.map((c, index) => (
          <button
            key={c.id}
            type="button"
            className="dropdown-item"
            ref={(el) => {
              customerSuggestionRefs.current[index] = el;
            }}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "8px 10px",
              border: "none",
              background: index === customerSuggestionIndex ? "#eff6ff" : "transparent",
              cursor: "pointer",
            }}
            onMouseEnter={() => setCustomerSuggestionIndex(index)}
            onMouseDown={(e) => {
              e.preventDefault();
              applyCustomerSuggestion(c);
            }}
          >
            <div style={{ fontWeight: 600 }}>{c.name || "Unnamed"}</div>
            <div style={{ fontSize: "0.8rem", color: "#475569" }}>{c.phone}</div>
          </button>
        ))}
      </div>
    ) : null;

  const applyRecentCustomerSuggestion = (option) => {
    if (!option) return;
    setCustomerFilter(option.label || "");
    setShowRecentCustomerSuggestions(false);
    setRecentCustomerSuggestionIndex(-1);
  };

  const startEditBill = (entry) => {
    if (!entry?.id) return;
    setActivePanel("billing");
    const { phone, name, address, address2 } = getEntryCustomerDetails(entry);

    setEditingBill({
      id: entry.id,
      billNo: entry.billNo || formatBillNo(entry.id),
      paymentDone: !!(entry.paymentDone ?? false),
      paymentType: normalizeBillPaymentType(entry.paymentType),
      discountAmount: normalizeDiscountValue(entry.discountAmount, Infinity),
    });
    setBillDate(entry.saleDate || new Date().toISOString().slice(0, 10));
    setDiscountAmount(
      Number(entry?.discountAmount || 0) > 0 ? String(normalizeDiscountValue(entry.discountAmount, Infinity)) : ""
    );

    skipLookupRef.current = true;
    setCustomerPhone(phone);
    setCustomerName(name);
    setCustomerAddress(address);
    setCustomerAddress2(address2);
    setTransporterName(String(entry?.transporterName || ""));
    setNumberOfBoxes(
      entry?.numberOfBoxes === null || entry?.numberOfBoxes === undefined ? "" : String(entry.numberOfBoxes)
    );
    setCustomerLookup({ status: "", message: "" });
    setShowCustomerModal(false);
    setNewCustomer(makeNewCustomerForm());
    setNewCustomerError("");

    setLines(
      padBillingLines((entry.items || []).map((it) => {
        const qty = Number(it?.qty) || 0;
        const rate = Number(it?.rate) || 0;
        const amount = +(qty * rate).toFixed(2);
        return {
          itemId: null,
          code: String(it?.itemCode || ""),
          itemGroup: String(it?.itemGroup || ""),
          name: String(it?.material || ""),
          boxNumber: String(it?.boxNumber || ""),
          qty,
          rate,
          amount,
        };
      }))
    );

    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const handleSaveCustomer = async () => {
    const name = newCustomer.name.trim();
    const phone = newCustomer.phone.trim();
    const address = newCustomer.address.trim();
    const address2 = newCustomer.address2.trim();
    if (!name || !phone) {
      setNewCustomerError("Customer name and mobile number are required.");
      return;
    }
    try {
      await addCustomer({
        name,
        phone,
        address,
        address2,
        source: BILLING_CATEGORY,
      });
      setCustomerPhone(phone);
      setCustomerName(name);
      setCustomerAddress(address);
      setCustomerAddress2(address2);
      setCustomerLookup({ status: "found", message: "Customer saved" });
      await refreshCustomers();
      setShowCustomerModal(false);
      setNewCustomer(makeNewCustomerForm());
      setNewCustomerError("");
    } catch (err) {
      console.error(err);
      const message = err?.message === "Phone already exists" ? "Phone already exists." : "Failed to save customer.";
      setNewCustomerError(message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (billSavingRef.current) {
      return;
    }
    const itemsPayload = lines
      .map((line) => {
        const qty = Number(line.qty) || 0;
        const rate = Number(line.rate) || 0;
        const amount = +(qty * rate).toFixed(2);
        const material = (line.name || line.code || "").trim();
        return {
          material,
          itemCode: String(line.code || "").trim(),
          itemGroup: String(line.itemGroup || "").trim(),
          boxNumber: String(line.boxNumber || "").trim(),
          qty,
          rate,
          amount,
        };
      })
      .filter((line) => line.material && (line.qty || line.rate));

    if (itemsPayload.length === 0) {
      alert("Please add at least one item to the bill.");
      return;
    }

    const grossTotal = itemsPayload.reduce((s, line) => s + (Number(line.amount) || 0), 0);
    const totalQty = itemsPayload.reduce((s, line) => s + (Number(line.qty) || 0), 0);
    const rawDiscountAmount = Number(discountAmount || 0);
    const appliedDiscountAmount = normalizeDiscountValue(discountAmount, grossTotal);
    if ((discountAmount || "").trim?.() === "" ? false : !Number.isFinite(rawDiscountAmount)) {
      alert("Enter a valid discount amount.");
      return;
    }
    if (rawDiscountAmount < 0 || rawDiscountAmount > grossTotal || appliedDiscountAmount > grossTotal) {
      alert("Enter a valid discount amount.");
      return;
    }
    const total = +(grossTotal - appliedDiscountAmount).toFixed(2);
    const numberOfBoxesText = String(numberOfBoxes || "").trim();
    const parsedNumberOfBoxes = numberOfBoxesText === "" ? null : Number(numberOfBoxesText);
    if (
      parsedNumberOfBoxes !== null &&
      (!Number.isInteger(parsedNumberOfBoxes) || parsedNumberOfBoxes < 0)
    ) {
      alert("Enter a valid total number of boxes.");
      return;
    }
    const phoneText = customerPhone.trim();
    const nameText = customerName.trim();
    const addressText = customerAddress.trim();
    const address2Text = customerAddress2.trim();
    const matchedCustomer =
      customers.find((customer) => String(customer?.phone || "").trim() === phoneText) ||
      customers.find(
        (customer) =>
          String(customer?.name || "").trim().toLowerCase() === nameText.toLowerCase() &&
          String(customer?.address || "").trim() === addressText
      );
    const resolvedAddress2Text = address2Text || String(matchedCustomer?.address2 || "").trim();
    const customerLines = buildCustomerLinesFromFields({
      name: nameText,
      address: addressText,
      address2: resolvedAddress2Text,
      phone: phoneText,
    });
    const customerText = customerLines.join("\n");

    billSavingRef.current = true;
    setBillSaving(true);
    try {
      const billPaymentType = normalizeBillPaymentType(editingBill?.paymentType);
      const billPaymentDone = billPaymentType === "Credit" ? false : !!(editingBill?.paymentDone ?? false);
      const payload = {
        category: BILLING_CATEGORY,
        saleDate: billDate,
        customer: customerText,
        customerName: nameText || null,
        customerPhone: phoneText || null,
        customerAddress: addressText || null,
        customerAddress2: resolvedAddress2Text || null,
        customerLatitude: matchedCustomer?.latitude ?? null,
        customerLongitude: matchedCustomer?.longitude ?? null,
        transporterName: transporterName.trim() || null,
        numberOfBoxes: parsedNumberOfBoxes,
        items: itemsPayload,
        paymentDone: billPaymentDone,
        paymentType: billPaymentType,
        discountAmount: appliedDiscountAmount,
        total,
        totalQty,
        payments: [],
      };
      if (editingBill?.id) await updateSalesEntry(editingBill.id, payload);
      else await addSalesEntry(payload);
      await refreshBills();
      clearBill();
      await refreshMyRequests();
    } catch (err) {
      console.error(err);
      alert(err?.message || (editingBill?.id ? "Failed to update bill." : "Failed to save bill."));
    } finally {
      billSavingRef.current = false;
      setBillSaving(false);
    }
  };

  const billingEntries = useMemo(
    () =>
      entries
        .filter((en) => en.category === BILLING_CATEGORY)
        .slice()
        .sort((a, b) => billSortValue(b) - billSortValue(a)),
    [entries, BILLING_CATEGORY]
  );

  const selectedCustomerPendingByDate = useMemo(() => {
    const selectedPhone = String(customerPhone || "").trim();
    const selectedName = String(customerName || "").trim().toLowerCase();
    if (!selectedPhone && !selectedName) {
      return { rows: [], billCount: 0, totalAmount: 0 };
    }

    const byDate = new Map();
    let billCount = 0;
    let totalAmount = 0;

    for (const en of billingEntries) {
      if (!en?.id) continue;
      if (editingBill?.id && en.id === editingBill.id) continue;
      if ((en.paymentDone ?? false) !== false) continue;

      const entryCustomer = String(en.customer || "");
      const entryPhone = extractPhone(entryCustomer);
      const entryName = extractCustomerPrimaryName(entryCustomer).toLowerCase();

      const phoneMatch = selectedPhone ? samePhoneMatch(selectedPhone, entryPhone) : false;
      const nameMatch = selectedName && entryName ? entryName === selectedName : false;

      // Prefer phone match when phone is available, with name fallback if old entries don't carry phone cleanly.
      if (!(phoneMatch || nameMatch)) continue;

      const dateKey = String(en.saleDate || "").trim() || "Unknown Date";
      const pendingAmount = getSettlementSummary(Number(en?.total || 0), en?.payments).pendingAmount;
      if (pendingAmount <= 0) continue;
      if (!byDate.has(dateKey)) {
        byDate.set(dateKey, { date: dateKey, billCount: 0, amount: 0, bills: [] });
      }
      const row = byDate.get(dateKey);
      row.billCount += 1;
      row.amount += pendingAmount;
      row.bills.push(en.billNo || formatBillNo(en.id));

      billCount += 1;
      totalAmount += pendingAmount;
    }

    const rows = Array.from(byDate.values())
      .map((row) => ({ ...row, amount: +Number(row.amount || 0).toFixed(2) }))
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));

    return { rows, billCount, totalAmount: +totalAmount.toFixed(2) };
  }, [billingEntries, customerPhone, customerName, editingBill?.id]);

  const buildReceiptForEntry = async (entry, opts = {}) => {
    const includeLogo = opts.includeLogo !== false;
    const billNo = entry.billNo || formatBillNo(entry.id);
    const billDate = entry.saleDate || "";
    const customerLines = buildCustomerLinesFromEntry(entry);
    const items = entry.items || [];
    const totalQty = entry.totalQty || 0;
    const discountAmount = normalizeDiscountValue(entry.discountAmount, Infinity);
    const totalAmount = entry.total || 0;
    const transporterName = entry.transporterName || "";
    const numberOfBoxes = entry.numberOfBoxes ?? null;
    const balanceAmount = entryPendingAmount(entry);
    const paymentMode = normalizeBillPaymentType(entry.paymentType);
    // Shared receipts load hosted images when opened, avoiding a large base64 upload.
    const logoUrl = includeLogo ? resolvePublicAsset("Logo.png") : "";
    const qrUrl = RECEIPT_CONFIG.showQrCode ? resolvePublicAsset("alifqr.PNG") : "";
    const gstDetails = await receiptGstDetails(customerLines);
    const html = buildReceiptHtml({
      ...gstDetails,
      logoUrl,
      qrUrl,
      billNo,
      billDate: formatDate(billDate),
      billTime: "",
      customerLines,
      items,
      totalQty,
      discountAmount,
      totalAmount,
      balanceAmount,
      paymentMode,
      transporterName,
      numberOfBoxes,
    });
    return {
      html,
      billNo,
      billDate,
      customerLines,
      transporterName,
      numberOfBoxes,
      items,
      totalQty,
      discountAmount,
      totalAmount,
    };
  };

  const recentBillingCustomerOptions = useMemo(() => {
    const map = new Map();
    for (const en of billingEntries) {
      const meta = recentCustomerFilterMeta(en?.customer || "");
      const key = meta.phone || meta.displayName.toLowerCase() || meta.label.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: meta.label,
          displayName: meta.displayName,
          phone: meta.phone,
          search: meta.search,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" })
    );
  }, [billingEntries]);

  const recentBillingCustomerSuggestions = useMemo(() => {
    const q = customerFilter.trim().toLowerCase();
    if (!q) return [];
    const filtered = recentBillingCustomerOptions.filter((option) => option.search.includes(q));
    return filtered.slice(0, 10);
  }, [recentBillingCustomerOptions, customerFilter]);

  useEffect(() => {
    if (!showRecentCustomerSuggestions || recentBillingCustomerSuggestions.length === 0) {
      setRecentCustomerSuggestionIndex(-1);
      return;
    }
    setRecentCustomerSuggestionIndex((prev) =>
      prev >= 0 && prev < recentBillingCustomerSuggestions.length ? prev : 0
    );
  }, [recentBillingCustomerSuggestions, showRecentCustomerSuggestions]);

  useEffect(() => {
    if (!showRecentCustomerSuggestions || recentCustomerSuggestionIndex < 0) return;
    const target = recentCustomerSuggestionRefs.current[recentCustomerSuggestionIndex];
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "nearest" });
    }
  }, [recentCustomerSuggestionIndex, recentBillingCustomerSuggestions, showRecentCustomerSuggestions]);

  const filteredBillingEntries = useMemo(() => {
    const q = customerFilter.trim().toLowerCase();
    if (!q) return billingEntries;
    return billingEntries.filter((en) => recentCustomerFilterMeta(en?.customer || "").search.includes(q));
  }, [billingEntries, customerFilter]);

  const hasCustomerFilter = customerFilter.trim().length > 0;

  const filteredCustomerTotals = useMemo(() => {
    if (!hasCustomerFilter) return null;
    return filteredBillingEntries.reduce(
      (acc, en) => {
        const qty = Number(en?.totalQty) || 0;
        const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
        const amount = settlementSummary.totalAmount;
        const paid = settlementSummary.paidAmount;
        acc.qty += qty;
        acc.amount += amount;
        acc.paid += paid;
        acc.balance += settlementSummary.pendingAmount;
        return acc;
      },
      { qty: 0, amount: 0, paid: 0, balance: 0 }
    );
  }, [filteredBillingEntries, hasCustomerFilter]);

  const filteredCustomerDateWiseRows = useMemo(() => {
    if (!hasCustomerFilter) return [];
    const map = new Map();
    for (const en of filteredBillingEntries) {
      const dateKey = String(en?.saleDate || "").trim() || "Unknown Date";
      const qty = Number(en?.totalQty) || 0;
      const settlementSummary = getSettlementSummary(Number(en?.total) || 0, en?.payments);
      const amount = settlementSummary.totalAmount;
      const paid = settlementSummary.paidAmount;
      const existing = map.get(dateKey) || {
        date: dateKey,
        entryCount: 0,
        totalQty: 0,
        totalAmount: 0,
        totalPaid: 0,
        balance: 0,
      };
      existing.entryCount += 1;
      existing.totalQty += qty;
      existing.totalAmount += amount;
      existing.totalPaid += paid;
      existing.balance += settlementSummary.pendingAmount;
      map.set(dateKey, existing);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.date === "Unknown Date") return 1;
      if (b.date === "Unknown Date") return -1;
      return String(a.date).localeCompare(String(b.date));
    });
  }, [filteredBillingEntries, hasCustomerFilter]);

  const filteredCustomerPaidDateWiseRows = useMemo(() => {
    if (!hasCustomerFilter) return [];
    const rows = [];
    for (const en of filteredBillingEntries) {
      for (const payment of Array.isArray(en?.payments) ? en.payments : []) {
        if (!isPaymentSettlement(payment)) continue;
        const amount = Number(payment?.amount) || 0;
        if (amount <= 0) continue;
        rows.push({
          paymentId: String(payment?.id || ""),
          entryId: String(en?.id || ""),
          date: String(payment?.date || "").trim(),
          totalPaid: amount,
          mode: normalizeBillPaymentType(payment?.mode || "Cash"),
          createdAt: String(payment?.created_at || payment?.createdAt || ""),
        });
      }
    }
    return rows.sort((a, b) => {
      const dateA = String(a.date || "");
      const dateB = String(b.date || "");
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const createdA = String(a.createdAt || "");
      const createdB = String(b.createdAt || "");
      if (createdA !== createdB) return createdA.localeCompare(createdB);
      return String(a.paymentId || "").localeCompare(String(b.paymentId || ""));
    });
  }, [filteredBillingEntries, hasCustomerFilter]);

  const customerPaidOnlyModal = customerDetailsModal.metric === "paid";
  const customerAmountModal = customerDetailsModal.metric === "amount";
  const customerDetailsRowsForModal = customerPaidOnlyModal
    ? filteredCustomerPaidDateWiseRows
    : filteredCustomerDateWiseRows;
  const customerDetailsTotalPages = Math.max(
    1,
    Math.ceil(customerDetailsRowsForModal.length / BILLING_CUSTOMER_MODAL_PAGE_SIZE)
  );
  const effectiveCustomerDetailsPage = Math.min(customerDetailsPage, customerDetailsTotalPages);
  const customerDetailsPageStart =
    customerDetailsRowsForModal.length === 0
      ? 0
      : (effectiveCustomerDetailsPage - 1) * BILLING_CUSTOMER_MODAL_PAGE_SIZE + 1;
  const customerDetailsPageEnd = Math.min(
    effectiveCustomerDetailsPage * BILLING_CUSTOMER_MODAL_PAGE_SIZE,
    customerDetailsRowsForModal.length
  );
  const customerDetailsPagedRows = customerDetailsRowsForModal.slice(
    (effectiveCustomerDetailsPage - 1) * BILLING_CUSTOMER_MODAL_PAGE_SIZE,
    effectiveCustomerDetailsPage * BILLING_CUSTOMER_MODAL_PAGE_SIZE
  );
  const customerDetailsColSpan = customerPaidOnlyModal ? 4 : customerAmountModal ? 5 : 6;

  useEffect(() => {
    if (!hasCustomerFilter) {
      setCustomerDetailsModal((prev) => (prev.open ? { ...prev, open: false } : prev));
      setCustomerDetailsPage(1);
      setCustomerPaidForm(makeCustomerPaidForm());
      setCustomerPaidSaving(false);
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      setCustomerPaidUpdating(false);
      setCustomerPaidDeleting({});
    }
  }, [hasCustomerFilter]);

  useEffect(() => {
    if (customerDetailsPage > customerDetailsTotalPages) {
      setCustomerDetailsPage(customerDetailsTotalPages);
    }
  }, [customerDetailsPage, customerDetailsTotalPages]);

  const openCustomerDetailsModal = (metric) => {
    if (!hasCustomerFilter) return;
    setCustomerDetailsModal({ open: true, metric });
    setCustomerDetailsPage(1);
    if (metric === "paid") {
      setCustomerPaidForm(makeCustomerPaidForm());
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      setCustomerPaidUpdating(false);
      setCustomerPaidDeleting({});
    }
  };

  const closeCustomerDetailsModal = () => {
    setCustomerDetailsModal((prev) => ({ ...prev, open: false }));
    setCustomerDetailsPage(1);
    setCustomerPaidForm(makeCustomerPaidForm());
    setCustomerPaidSaving(false);
    setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
    setCustomerPaidUpdating(false);
    setCustomerPaidDeleting({});
  };

  const handleAddCustomerPaidFromModal = async () => {
    if (!hasCustomerFilter) {
      alert("Please filter a customer first.");
      return;
    }

    const amount = Number(customerPaidForm.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(customerPaidForm.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    const pendingEntries = [...filteredBillingEntries]
      .map((en) => {
        const balance = getSettlementSummary(Number(en?.total || 0), en?.payments).pendingAmount;
        return { entry: en, balance };
      })
      .filter((row) => row.entry?.id && row.balance > 0)
      .sort((a, b) => {
        const dateCmp = String(a.entry?.saleDate || "").localeCompare(String(b.entry?.saleDate || ""));
        if (dateCmp !== 0) return dateCmp;
        return String(a.entry?.id || "").localeCompare(String(b.entry?.id || ""));
      });

    if (pendingEntries.length === 0) {
      alert("No pending balance available for this customer.");
      return;
    }

    const totalPending = pendingEntries.reduce((sum, row) => sum + row.balance, 0);
    if (amount > totalPending + 0.0001) {
      alert(`Amount exceeds pending balance (${totalPending.toFixed(2)}).`);
      return;
    }

    const allocations = [];
    let remaining = Number(amount.toFixed(2));
    for (let i = 0; i < pendingEntries.length && remaining > 0.0001; i += 1) {
      const row = pendingEntries[i];
      const isLast = i === pendingEntries.length - 1;
      const alloc = Math.min(row.balance, isLast ? remaining : Number(remaining.toFixed(2)));
      const rounded = Number(Math.min(row.balance, alloc).toFixed(2));
      if (rounded <= 0) continue;
      allocations.push({ entryId: row.entry.id, amount: rounded });
      remaining = Number((remaining - rounded).toFixed(2));
    }

    if (remaining > 0.0001) {
      alert("Unable to allocate full payment amount. Please try a smaller amount.");
      return;
    }

    setCustomerPaidSaving(true);
    let appliedCount = 0;
    try {
      for (const part of allocations) {
        await addSalesPayment(part.entryId, { amount: part.amount, date, mode: "Cash" });
        appliedCount += 1;
      }
      await refreshBills();
      setCustomerDetailsPage(1);
      setCustomerPaidForm((prev) => ({ ...prev, amount: "" }));
      alert(
        `Payment added successfully (${amount.toFixed(2)}) across ${appliedCount} bill${appliedCount === 1 ? "" : "s"}.`
      );
    } catch (err) {
      console.error(err);
      await refreshBills();
      alert(
        appliedCount > 0
          ? "Some payments were added before an error occurred. Data has been refreshed."
          : "Failed to add payment."
      );
    } finally {
      setCustomerPaidSaving(false);
    }
  };

  const handleStartEditCustomerPaid = (row) => {
    if (!row?.paymentId) {
      alert("Unable to edit this payment.");
      return;
    }
    setCustomerPaidEdit({
      paymentId: String(row.paymentId),
      date: String(row.date || new Date().toISOString().slice(0, 10)),
      amount: Number(row.totalPaid || 0).toFixed(2),
      mode: normalizeBillPaymentType(row.mode || "Cash"),
    });
  };

  const handleCancelEditCustomerPaid = () => {
    setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
    setCustomerPaidUpdating(false);
  };

  const handleDeleteCustomerPaid = async (row) => {
    const paymentId = String(row?.paymentId || "").trim();
    if (!paymentId) {
      alert("Unable to delete this payment.");
      return;
    }
    const amountText = Number(row?.totalPaid || 0).toFixed(2);
    const dateText = formatDisplayDate(row?.date);
    if (!window.confirm(`Delete paid entry of ${amountText} on ${dateText}?`)) {
      return;
    }

    setCustomerPaidDeleting((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await deleteSalesPayment(paymentId);
      await refreshBills();
      if (customerPaidEdit.paymentId === paymentId) {
        setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
        setCustomerPaidUpdating(false);
      }
      alert("Paid entry deleted.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to delete paid entry.");
    } finally {
      setCustomerPaidDeleting((prev) => {
        const next = { ...prev };
        delete next[paymentId];
        return next;
      });
    }
  };

  const handleSaveEditCustomerPaid = async () => {
    const paymentId = String(customerPaidEdit.paymentId || "").trim();
    if (!paymentId) return;
    const amount = Number(customerPaidEdit.amount) || 0;
    const today = new Date().toISOString().slice(0, 10);
    const date = String(customerPaidEdit.date || "").trim() || today;
    if (amount <= 0) {
      alert("Enter a valid paid amount.");
      return;
    }
    if (date > today) {
      alert("Payment date cannot be in the future.");
      return;
    }

    setCustomerPaidUpdating(true);
    try {
      await updateSalesPayment(paymentId, {
        amount: Number(amount.toFixed(2)),
        date,
        mode: normalizeBillPaymentType(customerPaidEdit.mode || "Cash"),
      });
      await refreshBills();
      setCustomerPaidEdit({ paymentId: "", date: "", amount: "", mode: "Cash" });
      alert("Paid amount updated.");
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update paid amount.");
    } finally {
      setCustomerPaidUpdating(false);
    }
  };

  useEffect(() => {
    setBillsPage(1);
  }, [customerFilter]);

  const billsTotalPages = useMemo(() => {
    const total = Math.ceil(filteredBillingEntries.length / BILLS_PER_PAGE);
    return total > 0 ? total : 1;
  }, [filteredBillingEntries.length]);

  useEffect(() => {
    setBillsPage((prev) => {
      if (prev < 1) return 1;
      if (prev > billsTotalPages) return billsTotalPages;
      return prev;
    });
  }, [billsTotalPages]);

  const pagedBillingEntries = useMemo(() => {
    const start = (billsPage - 1) * BILLS_PER_PAGE;
    return filteredBillingEntries.slice(start, start + BILLS_PER_PAGE);
  }, [filteredBillingEntries, billsPage]);

  const pageStartIndex = filteredBillingEntries.length === 0 ? 0 : (billsPage - 1) * BILLS_PER_PAGE + 1;
  const pageEndIndex =
    filteredBillingEntries.length === 0
      ? 0
      : Math.min((billsPage - 1) * BILLS_PER_PAGE + pagedBillingEntries.length, filteredBillingEntries.length);
  const markDoneModalEntry = useMemo(
    () =>
      entries.find((en) => String(en?.id || "") === String(markDonePaymentModal.entryId || "")) || null,
    [entries, markDonePaymentModal.entryId]
  );
  const markDoneModalPendingAmount = markDoneModalEntry
    ? entryPendingAmount(markDoneModalEntry)
    : Number(markDonePaymentModal.pendingAmount || 0);

  const handleSendWhatsApp = (entry) => {
    if (!entry || whatsappBusy.current) return;
    const customerText = entry.customer || "";
    const phone = extractPhone(customerText);
    if (!phone) {
      alert("No valid customer phone found for this bill.");
      return;
    }
    whatsappBusy.current = true;
    setWhatsappLoading(true);
    const whatsappWindow = window.open("", "_blank");
    if (!whatsappWindow) {
      whatsappBusy.current = false;
      setWhatsappLoading(false);
      alert("Please allow popups to send the WhatsApp message.");
      return;
    }
    whatsappWindow.opener = null;
    whatsappWindow.document.title = "Preparing WhatsApp receipt";
    whatsappWindow.document.body.textContent = "Preparing your receipt. Please wait...";
    buildReceiptForEntry(entry)
      .then(({
        html,
        billNo,
        billDate,
        customerLines,
        transporterName,
        numberOfBoxes,
        items,
        totalQty,
        discountAmount,
        totalAmount,
      }) => {
        return saveReceiptHtml(html).then((res) => ({
          res,
          billNo,
          billDate,
          customerLines,
          transporterName,
          numberOfBoxes,
          items,
          totalQty,
          discountAmount,
          totalAmount,
        }));
      })
      .then(({
        res,
        billNo,
        billDate,
        customerLines,
        transporterName,
        numberOfBoxes,
        items,
        totalQty,
        discountAmount,
        totalAmount,
      }) => {
        let receiptLink = "";
        if (res?.url) {
          if (res.url.startsWith("http")) {
            receiptLink = res.url;
          } else {
            const root = getApiBaseRoot().replace(/\/$/, "");
            const raw = String(res.url);
            const tail = raw.includes("/receipts/")
              ? raw.substring(raw.indexOf("/receipts/"))
              : "/" + raw.replace(/^\/+/, "");
            receiptLink = `${root}${tail}`;
          }
        }
        const waText = buildWhatsAppText({
          billNo,
          billDate,
          customerLines,
          transporterName,
          numberOfBoxes,
          items,
          totalQty,
          discountAmount,
          totalAmount,
          receiptLink,
        });
        const url = `https://wa.me/${phone}?text=${waText}`;
        if (whatsappWindow.closed) throw new Error("WhatsApp tab was closed. Please try again.");
        whatsappWindow.location.replace(url);
      })
      .catch((err) => {
        console.error(err);
        if (!whatsappWindow.closed) whatsappWindow.close();
        alert("Failed to prepare receipt for WhatsApp. Please try again.");
      }).finally(() => {
        whatsappBusy.current = false;
        setWhatsappLoading(false);
      });
  };

  const recentBillActionRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    flexWrap: "wrap",
    minWidth: 0,
  };

  const recentBillActionButtonStyle = {
    padding: "0.32rem 0.6rem",
    fontSize: "0.8rem",
    color: "#334155",
    background: "#ffffff",
    whiteSpace: "nowrap",
    minHeight: 34,
    justifyContent: "center",
    flexShrink: 0,
  };

  const recentBillDeleteButtonStyle = {
    width: 30,
    height: 30,
    padding: 0,
    flexShrink: 0,
  };

  return (
    <div className="stock-inward-container sales-billing-page">
      {whatsappLoading && createPortal(
        <div className="billing-whatsapp-overlay" role="status" aria-live="polite" aria-label="Preparing WhatsApp receipt" tabIndex={-1} ref={(node) => node?.focus()}>
          <div className="billing-whatsapp-loading-card">
            <span className="billing-whatsapp-spinner" aria-hidden="true" />
            <strong>Preparing WhatsApp receipt...</strong>
            <span>Please wait while we create your receipt link.</span>
          </div>
        </div>, document.body)}

      <header className="sales-screen-header">
        <div className="sales-screen-heading">
          <span className="sales-screen-kicker">ARF SEAFOODS · SALES WORKSPACE</span>
          <h1>{billingTitle}</h1>
          <p>Create a bill quickly, review totals, and manage previous sales from one screen.</p>
        </div>
        <div className="sales-workspace-tabs" role="tablist" aria-label={`${billingTitle} workspace`}>
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "billing"}
            aria-controls="sales-billing-panel"
            className={activePanel === "billing" ? "active" : ""}
            onClick={() => setActivePanel("billing")}
          >
            <span className="sales-tab-dot sales-tab-dot-new" aria-hidden="true" />
            New Sale
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "history"}
            aria-controls="sales-history-panel"
            className={activePanel === "history" ? "active" : ""}
            onClick={() => setActivePanel("history")}
          >
            <span className="sales-tab-dot sales-tab-dot-history" aria-hidden="true" />
            Sales History
            <span className="sales-tab-count">{billingEntries.length}</span>
          </button>
        </div>
      </header>

      <div
        id="sales-billing-panel"
        role="tabpanel"
        className="form-card sales-workspace-card"
        style={{
          width: "100%",
          maxWidth: 1440,
          margin: "0 auto 2rem",
          background: "linear-gradient(180deg, rgba(226, 232, 240, 0.96) 0%, rgba(241, 245, 249, 0.96) 100%)",
          border: "1px solid rgba(203, 213, 225, 0.95)",
          boxShadow: "0 12px 28px rgba(100, 116, 139, 0.18)",
          backdropFilter: "none",
        }}
      >
        <div className="card-header sales-workspace-card-header">
          <div>
            <span className="sales-card-kicker">BILL DETAILS</span>
            <h2 className="card-title">{editingBill ? "Update Sales Bill" : "Create Sales Bill"}</h2>
          </div>
          <div className="entry-count">{editingBill ? `Editing ${editingBill.billNo}` : "New Bill"}</div>
        </div>
        <form className="inward-form sales-billing-form" onSubmit={handleSubmit}>
          <div
            className="sales-customer-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div className="field sales-bill-date-field">
              <label htmlFor="sales-bill-date">Date</label>
              <input
                id="sales-bill-date"
                className="input-field"
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
              />
            </div>
            <div className="field sales-customer-phone-field">
              <label htmlFor="sales-customer-phone">Customer Phone</label>
              <div style={{ position: "relative" }}>
                <input
                  id="sales-customer-phone"
                  className="input-field"
                  type="text"
                  inputMode="numeric"
                  value={customerPhone}
                  onChange={(e) => {
                    setCustomerSuggestionAnchor("phone");
                    setCustomerPhone(e.target.value);
                    setCustomerAddress2("");
                  }}
                  onFocus={() => setCustomerSuggestionAnchor("phone")}
                  onBlur={() => lookupCustomer(customerPhone)}
                  onKeyDown={handleCustomerSuggestionKeyDown}
                  placeholder="Phone number"
                  autoComplete="off"
                />
                {customerSuggestionAnchor === "phone" ? renderCustomerSuggestions() : null}
              </div>
              {customerLookup.message ? (
                <div
                  style={{
                    marginTop: "4px",
                    fontSize: "0.75rem",
                    color:
                      customerLookup.status === "found"
                        ? "#16a34a"
                        : customerLookup.status === "notfound"
                          ? "#b91c1c"
                          : "#64748b",
                  }}
                >
                  {customerLookup.message}
                </div>
              ) : null}
              {selectedCustomerPendingByDate.rows.length > 0 ? (
                <div
                  style={{
                    marginTop: "8px",
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    borderRadius: 8,
                    padding: "8px",
                    color: "#7f1d1d",
                  }}
                >
                  <div style={{ fontSize: "0.78rem", fontWeight: 700 }}>
                    Pending Payment Alert ({selectedCustomerPendingByDate.billCount} bill
                    {selectedCustomerPendingByDate.billCount === 1 ? "" : "s"})
                  </div>
                  <div style={{ fontSize: "0.75rem", marginTop: 2 }}>
                    Total pending amount: {Number(selectedCustomerPendingByDate.totalAmount || 0).toFixed(2)}
                  </div>
                  <div style={{ marginTop: 6, display: "grid", gap: 4, maxHeight: 132, overflowY: "auto" }}>
                    {selectedCustomerPendingByDate.rows.map((row) => (
                      <div
                        key={row.date}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto auto",
                          gap: 8,
                          alignItems: "center",
                          fontSize: "0.75rem",
                          background: "rgba(255,255,255,0.65)",
                          border: "1px solid rgba(239,68,68,0.18)",
                          borderRadius: 6,
                          padding: "4px 6px",
                        }}
                        title={(row.bills || []).join(", ")}
                      >
                        <span style={{ fontWeight: 600 }}>{formatDisplayDate(row.date)}</span>
                        <span>{row.billCount} bill{row.billCount === 1 ? "" : "s"}</span>
                        <span style={{ fontWeight: 700 }}>{Number(row.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="field sales-customer-name-field">
              <label htmlFor="sales-customer-name">Customer Name</label>
              <div style={{ position: "relative" }}>
                <input
                  id="sales-customer-name"
                  className="input-field"
                  type="text"
                  value={customerName}
                  onChange={(e) => {
                    setCustomerSuggestionAnchor("name");
                    setCustomerName(e.target.value);
                    setCustomerAddress2("");
                  }}
                  onFocus={() => setCustomerSuggestionAnchor("name")}
                  onKeyDown={handleCustomerSuggestionKeyDown}
                  placeholder="Select customer..."
                  autoComplete="off"
                />
                {customerSuggestionAnchor === "name" ? renderCustomerSuggestions() : null}
              </div>
            </div>
            <div className="field sales-transporter-field">
              <label htmlFor="sales-transporter-name">Transporter Name</label>
              <input
                id="sales-transporter-name"
                className="input-field"
                type="text"
                maxLength={190}
                value={transporterName}
                onChange={(e) => setTransporterName(e.target.value)}
                placeholder="Transporter name"
                autoComplete="off"
              />
            </div>
            <div className="field sales-total-boxes-field">
              <label htmlFor="sales-total-boxes">Total Number of Boxes</label>
              <input
                id="sales-total-boxes"
                className="input-field numeric no-spinner"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={numberOfBoxes}
                onChange={(e) => setNumberOfBoxes(e.target.value)}
                placeholder="Total boxes"
              />
            </div>
            <div className="field sales-address-field">
              <label htmlFor="sales-customer-address">Address</label>
              <textarea
                id="sales-customer-address"
                className="input-field"
                rows={2}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder="Address"
              />
            </div>
          </div>

          <div
            className="sales-work-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: "16px",
            }}
          >
            <section className="sales-catalog-panel" aria-hidden="true">
              <div className="sales-panel-title-row">
                <div>
                  <span>STEP 1</span>
                  <h3>Choose Items</h3>
                </div>
                <button type="button" className="sales-refresh-button" onClick={refreshItems}>
                  Refresh
                </button>
              </div>
              <div className="sales-catalog-toolbar">
                <div className="sales-item-search" style={{ position: "relative", maxWidth: 280, width: "100%" }}>
                  <input
                    type="search"
                    className="input-field"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setShowItemSuggestions(e.target.value.trim() !== "");
                    }}
                    onFocus={() => {
                      if (query.trim()) {
                        setShowItemSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setShowItemSuggestions(false);
                      }, 120);
                    }}
                    onKeyDown={handleItemSearchKeyDown}
                    placeholder="Search items..."
                    style={{ maxWidth: 280 }}
                    ref={searchRef}
                    autoComplete="off"
                  />
                  {showItemSuggestions && query.trim() && filteredItems.length > 0 ? (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        marginTop: 4,
                        zIndex: 20,
                        maxHeight: 220,
                        overflowY: "auto",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                      }}
                    >
                      {filteredItems.map((item, index) => (
                        <button
                          key={item.id || item.code || item.name}
                          type="button"
                          ref={(el) => {
                            itemSuggestionRefs.current[index] = el;
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 10px",
                            border: "none",
                            background: index === itemSuggestionIndex ? "#eff6ff" : "transparent",
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setItemSuggestionIndex(index)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            applyItemSuggestion(item);
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>{item.name || item.code}</div>
                          <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                            {item.code ? `${item.code}` : ""}{" "}
                            {item.sellingPrice ? `| Rs ${Number(item.sellingPrice).toFixed(2)}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <button type="button" className="btn-secondary" onClick={addCustomLine}>
                  + Custom Item
                </button>
              </div>

              <div className="table-container billing-items-container sales-catalog-table" style={{ marginBottom: 0 }}>
                <table className="entries-table" style={{ minWidth: 0 }}>
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "36%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "28%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Item</th>
                      <th className="text-right">Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="empty-state" style={{ padding: "16px" }}>
                          No items found
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.code}</td>
                          <td>{item.name}</td>
                          <td className="text-right">{Number(item.sellingPrice || 0).toFixed(2)}</td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => addItemToBill(item)}
                              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                            >
                              Add
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="sales-cart-panel">
              <div className="sales-panel-title-row">
                <div>
                  <span>STEP 2</span>
                  <h3>Current Bill</h3>
                </div>
                <span className="sales-line-count">{totals.itemCount} {totals.itemCount === 1 ? "item" : "items"}</span>
              </div>
              <div className="table-container sales-cart-table" style={{ marginBottom: 0 }}>
                <table className="modern-table" style={{ minWidth: 0 }}>
                  <colgroup>
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "50px" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="highlighted-col">Material</th>
                      <th>Box Number</th>
                      <th>Quantity</th>
                      <th className="highlighted-col">Rate</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, i) => {
                      const materialQuery = String(line?.name || "").trim().toLowerCase();
                      const materialOptions =
                        materialQuery.length >= 2
                          ? billingItems
                              .filter(
                                (item) =>
                                  String(item?.name || "").toLowerCase().includes(materialQuery) ||
                                  String(item?.code || "").toLowerCase().includes(materialQuery)
                              )
                              .slice(0, 20)
                          : [];
                      return (
                        <tr key={`${line.itemId || "custom"}-${i}`}>
                          <td>
                            <div
                              className="sales-material-input-wrap"
                              ref={(element) => {
                                materialLineRefs.current[i] = element;
                              }}
                            >
                              <input
                                className="input-field"
                                type="text"
                                value={line.name}
                                onChange={(e) => {
                                  updateLine(i, "name", e.target.value);
                                  openMaterialLineSuggestions(i, e.target.value);
                                }}
                                onFocus={(e) => openMaterialLineSuggestions(i, e.target.value)}
                                onBlur={(event) => {
                                  const itemName = event.currentTarget.value;
                                  setTimeout(closeMaterialLineSuggestions, 120);
                                  void ensureBillingItemForLine(i, itemName);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "ArrowDown" && materialOptions.length > 0) {
                                    e.preventDefault();
                                    if (openMaterialLineIndex !== i) {
                                      openMaterialLineSuggestions(i, line.name);
                                    } else {
                                      setActiveMaterialLineSuggestionIndex((current) =>
                                        Math.min(current + 1, materialOptions.length - 1)
                                      );
                                    }
                                    return;
                                  }
                                  if (e.key === "ArrowUp" && materialOptions.length > 0) {
                                    e.preventDefault();
                                    setActiveMaterialLineSuggestionIndex((current) => Math.max(current - 1, 0));
                                    return;
                                  }
                                  if (e.key === "Escape") {
                                    closeMaterialLineSuggestions();
                                    return;
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (openMaterialLineIndex === i && materialOptions.length > 0) {
                                      selectMaterialForLine(
                                        i,
                                        materialOptions[
                                          activeMaterialLineSuggestionIndex >= 0
                                            ? activeMaterialLineSuggestionIndex
                                            : 0
                                        ]
                                      );
                                      return;
                                    }
                                    qtyRefs.current[i]?.focus?.();
                                    qtyRefs.current[i]?.select?.();
                                  }
                                }}
                                placeholder="Material"
                                autoComplete="off"
                                data-sales-material-row={i}
                              />
                              {openMaterialLineIndex === i &&
                              materialOptions.length > 0 &&
                              materialLineDropdownRect ? (
                                <PortalDropdown
                                  className="autocomplete-dropdown sales-material-dropdown"
                                  style={{
                                    position: "absolute",
                                    top: materialLineDropdownRect.top,
                                    left: materialLineDropdownRect.left,
                                    width: materialLineDropdownRect.width,
                                  }}
                                >
                                  {materialOptions.map((item, optionIndex) => (
                                    <button
                                      key={item.id || item.code || item.name}
                                      type="button"
                                      ref={(element) => {
                                        materialLineSuggestionRefs.current[optionIndex] = element;
                                      }}
                                      className={
                                        optionIndex === activeMaterialLineSuggestionIndex ? "is-active" : ""
                                      }
                                      onMouseEnter={() => setActiveMaterialLineSuggestionIndex(optionIndex)}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        selectMaterialForLine(i, item);
                                      }}
                                    >
                                      <strong>{item.name || item.code}</strong>
                                      <span>
                                        {[item.code, `Rs ${Number(item.sellingPrice || 0).toFixed(2)}`]
                                          .filter(Boolean)
                                          .join(" | ")}
                                      </span>
                                    </button>
                                  ))}
                                </PortalDropdown>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <input
                              className="input-field"
                              type="text"
                              maxLength={64}
                              value={line.boxNumber}
                              placeholder="Box No."
                              aria-label={`Box Number ${i + 1}`}
                              onChange={(e) => updateLine(i, "boxNumber", e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const target = qtyRefs.current[i];
                                  target?.focus?.();
                                  target?.select?.();
                                }
                              }}
                              ref={(el) => {
                                if (el) {
                                  boxNumberRefs.current[i] = el;
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input
                              className="input-field numeric no-spinner"
                              type="number"
                              step="any"
                              value={line.qty}
                              placeholder="0"
                              onChange={(e) => updateLine(i, "qty", e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const target = rateRefs.current[i];
                                  if (target) {
                                    target.focus();
                                    if (typeof target.select === "function") {
                                      target.select();
                                    }
                                  }
                                }
                              }}
                              ref={(el) => {
                                if (el) {
                                  qtyRefs.current[i] = el;
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input
                              className="input-field numeric"
                              type="number"
                              step="any"
                              value={line.rate}
                              placeholder="0.00"
                              onChange={(e) => updateLine(i, "rate", e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const target = materialLineRefs.current[i + 1]?.querySelector("input");
                                  if (target) {
                                    target.focus();
                                    if (typeof target.select === "function") {
                                      target.select();
                                    }
                                  }
                                }
                              }}
                              ref={(el) => {
                                if (el) {
                                  rateRefs.current[i] = el;
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input className="input-field numeric calculated" type="number" readOnly value={line.amount} />
                          </td>
                          <td className="actions">
                            <button type="button" className="btn-remove" title="Remove" onClick={() => removeLine(i)}>
                              x
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan={3}></td>
                      <td className="total-label">Total</td>
                      <td>
                        <input
                          className="input-field total-amount numeric"
                          type="number"
                          readOnly
                          value={totals.grossAmount.toFixed(2)}
                        />
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div
                className="sales-discount-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "12px",
                  alignItems: "end",
                }}
              >
                <div className="field">
                  <label>Discount Value</label>
                  <input
                    className="input-field numeric"
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div
                  className="sales-discount-card"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: "1px solid #fed7aa",
                    background: "#fff7ed",
                    color: "#9a3412",
                    minHeight: 70,
                  }}
                >
                  <div style={{ fontSize: "0.76rem", fontWeight: 700 }}>Applied Discount</div>
                  <div style={{ marginTop: 6, fontSize: "1.12rem", fontWeight: 900 }}>
                    {totals.discountAmount.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="payment-summary sales-total-summary" style={{ justifyContent: "flex-end" }}>
                <span className="summary-item">
                  <span className="label">Items:</span>
                  <span className="value">{totals.itemCount}</span>
                </span>
                <span className="summary-item">
                  <span className="label">Qty:</span>
                  <span className="value">{totals.totalQty}</span>
                </span>
                <span className="summary-item">
                  <span className="label">Gross:</span>
                  <span className="value">{totals.grossAmount.toFixed(2)}</span>
                </span>
                <span className="summary-item">
                  <span className="label">Discount:</span>
                  <span className="value" style={{ color: "#c2410c" }}>{totals.discountAmount.toFixed(2)}</span>
                </span>
                <span className="summary-item">
                  <span className="label">Net Total:</span>
                  <span className="value paid">{totals.totalAmount.toFixed(2)}</span>
                </span>
              </div>

              <div className="form-actions sales-form-actions">
                <button type="button" className="btn-secondary" onClick={addCustomLine} disabled={billSaving}>
                  <span className="btn-icon">+</span>
                  Add Row
                </button>
                <button type="button" className="btn-secondary" onClick={refreshCustomers} disabled={billSaving}>
                  Refresh Customers
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={clearBill}
                  disabled={billSaving || (!editingBill && totals.itemCount === 0)}
                >
                  {editingBill ? "Cancel" : "Clear Bill"}
                </button>
                <button type="submit" className="btn-primary" disabled={billSaving || totals.itemCount === 0}>
                  {billSaving ? (editingBill ? "Updating..." : "Saving...") : editingBill ? "Update Sale" : "Save Sale"}
                </button>
              </div>
            </section>
          </div>
        </form>
      </div>

      {showCustomerModal ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card">
            <div className="modal-title">Add New Customer</div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="field fl-field">
                  <input
                    id="new-customer-name"
                    className="fl-input input-field"
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="new-customer-name" className="fl-label">Customer Name</label>
                </div>
                <div className="field fl-field">
                  <input
                    id="new-customer-phone"
                    className="fl-input input-field"
                    type="text"
                    inputMode="numeric"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    placeholder=" "
                    required
                  />
                  <label htmlFor="new-customer-phone" className="fl-label">Mobile Number</label>
                </div>
                <div className="field full">
                  <label htmlFor="new-customer-address">Address</label>
                  <textarea
                    id="new-customer-address"
                    className="input-field"
                    rows={2}
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                    placeholder="Customer address"
                  />
                </div>
                <div className="field full">
                  <label htmlFor="new-customer-address-2">Address 2 (optional)</label>
                  <textarea
                    id="new-customer-address-2"
                    className="input-field"
                    rows={2}
                    value={newCustomer.address2}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address2: e.target.value })}
                    placeholder="Manual landmark / flat / door details"
                  />
                </div>
              </div>
              {newCustomerError ? (
                <div style={{ marginTop: "8px", color: "#b91c1c", fontSize: "0.875rem" }}>
                  {newCustomerError}
                </div>
              ) : null}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowCustomerModal(false);
                  setNewCustomerError("");
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveCustomer}>
                Save Customer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {markDonePaymentModal.open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeMarkDonePaymentModal}>
          <div className="modal-card" style={{ width: "min(520px, 100%)" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Add Payment - {markDonePaymentModal.billNo || "Bill"}</div>
            <div className="modal-body">
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #dbeafe",
                  background: "#f8fbff",
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ color: "#475569", fontWeight: 700 }}>Pending Amount</span>
                <span style={{ color: "#b91c1c", fontWeight: 800 }}>
                  {Number(markDoneModalPendingAmount || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div className="field">
                  <label>Date</label>
                  <input
                    className="input-field"
                    type="date"
                    max={new Date().toISOString().slice(0, 10)}
                    value={markDonePaymentForm.date}
                    onChange={(e) => setMarkDonePaymentForm((prev) => ({ ...prev, date: e.target.value }))}
                    disabled={markDonePaymentSaving}
                  />
                </div>
                <div className="field">
                  <label>Paid Amount</label>
                  <input
                    className="input-field numeric"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={markDonePaymentForm.amount}
                    onChange={(e) => setMarkDonePaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    disabled={markDonePaymentSaving}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeMarkDonePaymentModal} disabled={markDonePaymentSaving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={submitMarkDonePaymentModal} disabled={markDonePaymentSaving}>
                {markDonePaymentSaving ? "Saving..." : "Save Payment"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div
        id="sales-history-panel"
        role="tabpanel"
        className="entries-card sales-history-card"
        style={{
          width: "100%",
          maxWidth: 1440,
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
          margin: "0 auto 2rem",
          background: "linear-gradient(180deg, rgba(226, 232, 240, 0.96) 0%, rgba(241, 245, 249, 0.96) 100%)",
          border: "1px solid rgba(203, 213, 225, 0.95)",
          boxShadow: "0 12px 28px rgba(100, 116, 139, 0.18)",
          backdropFilter: "none",
        }}
      >
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Recent Bills</h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative", minWidth: 220, flex: "0 1 320px" }}>
              <input
                className="input-field"
                type="text"
                value={customerFilter}
                onChange={(e) => {
                  setCustomerFilter(e.target.value);
                  setShowRecentCustomerSuggestions(true);
                }}
                onFocus={() => setShowRecentCustomerSuggestions(true)}
                onBlur={() => {
                  setRecentCustomerSuggestionIndex(-1);
                  setTimeout(() => setShowRecentCustomerSuggestions(false), 140);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" && showRecentCustomerSuggestions && recentBillingCustomerSuggestions.length > 0) {
                    e.preventDefault();
                    setRecentCustomerSuggestionIndex((prev) =>
                      Math.min(prev + 1, recentBillingCustomerSuggestions.length - 1)
                    );
                    return;
                  }
                  if (e.key === "ArrowUp" && showRecentCustomerSuggestions && recentBillingCustomerSuggestions.length > 0) {
                    e.preventDefault();
                    setRecentCustomerSuggestionIndex((prev) => Math.max(prev - 1, 0));
                    return;
                  }
                  if (e.key === "Escape") {
                    setShowRecentCustomerSuggestions(false);
                    setRecentCustomerSuggestionIndex(-1);
                  }
                  if (e.key === "Enter" && showRecentCustomerSuggestions && recentBillingCustomerSuggestions.length > 0) {
                    e.preventDefault();
                    applyRecentCustomerSuggestion(
                      recentBillingCustomerSuggestions[
                        recentCustomerSuggestionIndex >= 0 ? recentCustomerSuggestionIndex : 0
                      ]
                    );
                  }
                }}
                placeholder="Filter by customer name / phone"
                style={{ minWidth: 180 }}
                autoComplete="off"
              />
              {showRecentCustomerSuggestions && recentBillingCustomerSuggestions.length > 0 ? (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    maxHeight: 260,
                    overflowY: "auto",
                    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.14)",
                    zIndex: 30,
                  }}
                >
                  {recentBillingCustomerSuggestions.map((option, index) => (
                    <button
                      key={option.key}
                      type="button"
                      ref={(el) => {
                        recentCustomerSuggestionRefs.current[index] = el;
                      }}
                      style={{
                        width: "100%",
                        border: "none",
                        background: index === recentCustomerSuggestionIndex ? "#eff6ff" : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        padding: "8px 10px",
                      }}
                      onMouseEnter={() => setRecentCustomerSuggestionIndex(index)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        applyRecentCustomerSuggestion(option);
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.86rem" }}>{option.displayName}</div>
                      <div style={{ fontSize: "0.78rem", color: "#475569" }}>
                        {option.phone || "No phone"} {option.phone ? "| " : ""} {option.label}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                refreshBills();
                refreshMyRequests();
              }}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Refresh
            </button>
            <span className="entry-count">
              {filteredBillingEntries.length}
              {customerFilter.trim() ? ` / ${billingEntries.length}` : ""} bills
            </span>
          </div>
        </div>
        {hasCustomerFilter && filteredCustomerTotals ? (
          <div style={{ padding: "4px 16px 12px" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "stretch", flexWrap: "wrap" }}>
              <div
                style={{
                  padding: "10px 14px",
                  minWidth: 150,
                  textAlign: "left",
                  borderRadius: 14,
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Qty</div>
                <div style={{ marginTop: 4, fontWeight: 800, fontSize: "1.05rem", color: "#475569" }}>
                  {Number(filteredCustomerTotals.qty || 0).toFixed(2)}
                </div>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => openCustomerDetailsModal("amount")}
                title="Open date-wise amount details"
                style={{
                  padding: "10px 14px",
                  minWidth: 180,
                  textAlign: "left",
                  borderRadius: 14,
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Amount</div>
                <div style={{ marginTop: 4, fontWeight: 800, fontSize: "1.05rem", color: "#475569" }}>
                  {Number(filteredCustomerTotals.amount || 0).toFixed(2)}
                </div>
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => openCustomerDetailsModal("paid")}
                title="Open date-wise paid details"
                style={{
                  padding: "10px 14px",
                  minWidth: 150,
                  textAlign: "left",
                  borderRadius: 14,
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Total Paid</div>
                <div style={{ marginTop: 4, fontWeight: 800, fontSize: "1.05rem", color: "#16a34a" }}>
                  {Number(filteredCustomerTotals.paid || 0).toFixed(2)}
                </div>
              </button>
              <div
                style={{
                  padding: "10px 14px",
                  minWidth: 160,
                  textAlign: "left",
                  borderRadius: 14,
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Balance</div>
                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 800,
                    fontSize: "1.05rem",
                    color: Number(filteredCustomerTotals.balance || 0) > 0 ? "#b91c1c" : "#16a34a",
                  }}
                >
                  {Number(filteredCustomerTotals.balance || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className="entries-table-container">
          <div
            className="billing-recent-bills-scroll"
            style={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              boxSizing: "border-box",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table
              className="entries-table billing-recent-bills-table"
              style={{
                minWidth: "1354px",
                width: "1354px",
                borderCollapse: "separate",
                borderSpacing: 0,
                tableLayout: "fixed",
              }}
            >
            <colgroup>
              <col style={{ width: "52px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "260px" }} />
              <col style={{ width: "72px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "220px" }} />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Bill No</th>
                <th>Date</th>
                <th>Customer</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Discount</th>
                <th>Payment</th>
                <th>Payment Type</th>
                <th className="billing-actions-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBillingEntries.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-state" style={{ padding: "16px" }}>
                    {customerFilter.trim() ? "No bills match this customer" : "No bills yet"}
                  </td>
                </tr>
              ) : (
                pagedBillingEntries.map((en) => {
                  const isOpen = !!expanded[en.id];
                  const editReq = requestByEntry[en.id]?.edit;
                  const delReq = requestByEntry[en.id]?.delete;
                  const editKey = requestKey(en.id, "edit");
                  const delKey = requestKey(en.id, "delete");
                  const editStatus = String(editReq?.status || "");
                  const delStatus = String(delReq?.status || "");
                  const editBusy = !!billRequesting[editKey];
                  const delBusy = !!billRequesting[delKey];
                  return (
                    <React.Fragment key={en.id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="expander"
                            onClick={() => setExpanded((ex) => ({ ...ex, [en.id]: !isOpen }))}
                          >
                            {isOpen ? "v" : ">"}
                          </button>
                        </td>
                        <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{en.billNo || formatBillNo(en.id)}</td>
                        <td>{formatDisplayDate(en.saleDate)}</td>
                        <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{en.customer || "-"}</td>
                        <td className="text-right">{en.totalQty}</td>
                        <td className="text-right">{Number(en.total).toFixed(2)}</td>
                        <td className="text-right">{Number(en.discountAmount || 0).toFixed(2)}</td>
                        <td>
                          {canUpdatePaymentStatus ? (
                            <select
                              className="input-field compact"
                              value={(en.paymentDone ?? false) ? "done" : "notdone"}
                              disabled={!!paymentUpdating[en.id] || !!paymentTypeUpdating[en.id]}
                              style={{ width: "100%", minWidth: 0 }}
                              onChange={(e) => {
                                const prevDone = en.paymentDone ?? false;
                                const nextDone = e.target.value === "done";
                                if (!prevDone && nextDone) {
                                  openMarkDonePaymentModal(en);
                                  return;
                                }
                                void setPaymentDone(en.id, prevDone, nextDone);
                              }}
                            >
                              <option value="done">Done</option>
                              <option value="notdone">Not Done</option>
                            </select>
                          ) : (
                            <span
                              style={{
                                fontWeight: 600,
                                color: (en.paymentDone ?? false) ? "#16a34a" : "#b91c1c",
                              }}
                            >
                              {(en.paymentDone ?? false) ? "Done" : "Not Done"}
                            </span>
                          )}
                        </td>
                        <td>
                          {canUpdatePaymentStatus ? (
                            <select
                              className="input-field compact"
                              value={normalizeBillPaymentType(en.paymentType)}
                              disabled={!!paymentTypeUpdating[en.id] || !!paymentUpdating[en.id]}
                              style={{ width: "100%", minWidth: 0 }}
                              onChange={(e) =>
                                setPaymentType(
                                  en.id,
                                  en.paymentType,
                                  en.paymentDone ?? false,
                                  e.target.value
                                )
                              }
                            >
                              {BILL_PAYMENT_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              style={{
                                fontWeight: 600,
                                color:
                                  normalizeBillPaymentType(en.paymentType) === "Credit"
                                    ? "#b91c1c"
                                    : normalizeBillPaymentType(en.paymentType) === "UPI"
                                      ? "#1d4ed8"
                                      : "#334155",
                              }}
                            >
                              {normalizeBillPaymentType(en.paymentType)}
                            </span>
                          )}
                        </td>
                        <td className="billing-actions-cell">
                          <div style={recentBillActionRowStyle}>

                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => handlePrintEntry(en)}
                              style={recentBillActionButtonStyle}
                            >
                              Print
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => handleLedgerPrintEntry(en)}
                              style={recentBillActionButtonStyle}
                            >
                              Ledger Print
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => handleSendWhatsApp(en)}
                              style={recentBillActionButtonStyle}
                            >
                              WhatsApp
                            </button>
                            {isAdmin ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  title="Edit bill"
                                  onClick={() => startEditBill(en)}
                                  disabled={!!billDeleting[en.id]}
                                  style={recentBillActionButtonStyle}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn-remove"
                                  title="Delete bill"
                                  onClick={() => handleDeleteBill(en)}
                                  disabled={!!billDeleting[en.id]}
                                  style={recentBillDeleteButtonStyle}
                                >
                                  x
                                </button>
                              </>
                            ) : (
                              <>
                                {editStatus === "approved" ? (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    title="Edit bill (approved)"
                                    onClick={() => startEditBill(en)}
                                    disabled={!!billDeleting[en.id]}
                                    style={recentBillActionButtonStyle}
                                  >
                                    Edit
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => requestBillAction(en, "edit")}
                                    disabled={editBusy || editStatus === "pending"}
                                    style={recentBillActionButtonStyle}
                                    title={editStatus === "pending" ? "Edit request pending" : "Request edit"}
                                  >
                                    {editStatus === "pending" ? "Edit Pending" : "Request Edit"}
                                  </button>
                                )}

                                {delStatus === "approved" ? (
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    title="Delete bill (approved)"
                                    onClick={() => handleDeleteBill(en)}
                                    disabled={!!billDeleting[en.id]}
                                    style={recentBillDeleteButtonStyle}
                                  >
                                    x
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => requestBillAction(en, "delete")}
                                    disabled={delBusy || delStatus === "pending"}
                                    style={recentBillActionButtonStyle}
                                    title={delStatus === "pending" ? "Delete request pending" : "Request delete"}
                                  >
                                    {delStatus === "pending" ? "Delete Pending" : "Request Delete"}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td></td>
                          <td colSpan={10}>
                            <div className="items-section" style={{ marginTop: "8px" }}>
                              <div className="section-title">Items</div>
                              {(en.transporterName ||
                                (en.numberOfBoxes !== null && en.numberOfBoxes !== undefined)) && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 18,
                                    flexWrap: "wrap",
                                    margin: "8px 0",
                                    fontSize: "0.84rem",
                                    fontWeight: 700,
                                    color: "#334155",
                                  }}
                                >
                                  {en.transporterName ? <span>Transporter: {en.transporterName}</span> : null}
                                  {en.numberOfBoxes !== null && en.numberOfBoxes !== undefined ? (
                                    <span>Total Boxes: {en.numberOfBoxes}</span>
                                  ) : null}
                                </div>
                              )}
                              <table className="items-table">
                                <colgroup>
                                  <col style={{ width: "240px" }} />
                                  <col style={{ width: "90px" }} />
                                  <col style={{ width: "90px" }} />
                                  <col style={{ width: "110px" }} />
                                  <col style={{ width: "110px" }} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th>Item</th>
                                    <th>Box Number</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Rate</th>
                                    <th className="text-right">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {en.items.map((it, idx) => (
                                    <tr key={idx}>
                                      <td>{it.material}</td>
                                      <td>{it.boxNumber || "-"}</td>
                                      <td className="text-right">{it.qty}</td>
                                      <td className="text-right">{Number(it.rate).toFixed(2)}</td>
                                      <td className="text-right">{Number(it.amount).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: 18,
                                  flexWrap: "wrap",
                                  marginTop: 10,
                                  fontSize: "0.86rem",
                                  fontWeight: 700,
                                  color: "#334155",
                                }}
                              >
                                <span>Gross: {calcGrossAmount(en.items || []).toFixed(2)}</span>
                                <span style={{ color: "#c2410c" }}>
                                  Discount: {Number(en.discountAmount || 0).toFixed(2)}
                                </span>
                                <span style={{ color: "#166534" }}>
                                  Net Total: {Number(en.total || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            </table>
          </div>
        </div>
        <div
          style={{
            padding: "0 1.5rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: "0.82rem", color: "#475569" }}>
            {`Showing ${pageStartIndex}-${pageEndIndex} of ${filteredBillingEntries.length} bills`}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setBillsPage((prev) => Math.max(1, prev - 1))}
              disabled={billsPage <= 1}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Prev
            </button>
            <span className="entry-count">{`Page ${billsPage} / ${billsTotalPages}`}</span>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setBillsPage((prev) => Math.min(billsTotalPages, prev + 1))}
              disabled={billsPage >= billsTotalPages}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {hasCustomerFilter && customerDetailsModal.open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={closeCustomerDetailsModal}>
          <div
            className="modal-card"
            style={{ width: "min(860px, 100%)", maxHeight: "85vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-title">Customer Date-wise Details - {customerFilter.trim()}</div>
            <div className="modal-body">
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: 12,
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ color: "#475569" }}>
                  Opened from: <strong>{customerPaidOnlyModal ? "Total Paid" : "Total Amount"}</strong>
                </span>
                {customerPaidOnlyModal ? (
                  <span style={{ marginLeft: "auto", fontWeight: 700, color: "#16a34a" }}>
                    Paid: {Number(filteredCustomerTotals?.paid || 0).toFixed(2)}
                  </span>
                ) : (
                  <>
                    <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                      Qty: {Number(filteredCustomerTotals?.qty || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      Amount: {Number(filteredCustomerTotals?.amount || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>
                      Paid: {Number(filteredCustomerTotals?.paid || 0).toFixed(2)}
                    </span>
                    <span style={{ fontWeight: 700, color: "#b91c1c" }}>
                      Balance: {Number(filteredCustomerTotals?.balance || 0).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {customerPaidOnlyModal ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 12,
                    padding: "10px",
                    border: "1px solid #dbeafe",
                    background: "#f8fbff",
                    borderRadius: 10,
                  }}
                >
                  <input
                    type="date"
                    className="input-field compact"
                    max={new Date().toISOString().slice(0, 10)}
                    value={customerPaidForm.date}
                    onChange={(e) => setCustomerPaidForm((prev) => ({ ...prev, date: e.target.value }))}
                    style={{ width: 160 }}
                    disabled={customerPaidSaving}
                  />
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    className="input-field compact numeric"
                    placeholder="Paid amount"
                    value={customerPaidForm.amount}
                    onChange={(e) => setCustomerPaidForm((prev) => ({ ...prev, amount: e.target.value }))}
                    style={{ width: 180 }}
                    disabled={customerPaidSaving}
                  />
                  <button
                    type="button"
                    className="btn-add-payment"
                    onClick={handleAddCustomerPaidFromModal}
                    disabled={customerPaidSaving}
                  >
                    {customerPaidSaving ? "Saving..." : "Add Paid Amount"}
                  </button>
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                  Showing {customerDetailsPageStart}-{customerDetailsPageEnd} of {customerDetailsRowsForModal.length}
                </div>
                {customerDetailsTotalPages > 1 ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setCustomerDetailsPage((p) => Math.max(1, p - 1))}
                      disabled={effectiveCustomerDetailsPage <= 1}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Prev
                    </button>
                    <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                      Page {effectiveCustomerDetailsPage} / {customerDetailsTotalPages}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setCustomerDetailsPage((p) => Math.min(customerDetailsTotalPages, p + 1))}
                      disabled={effectiveCustomerDetailsPage >= customerDetailsTotalPages}
                      style={{ padding: "0.3rem 0.65rem", fontSize: "0.75rem" }}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="entries-table-container">
                <table className="entries-table" style={{ minWidth: customerPaidOnlyModal ? 560 : 720 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      {customerPaidOnlyModal ? <th>Mode</th> : null}
                      {!customerPaidOnlyModal ? <th className="text-right">Entries</th> : null}
                      {!customerPaidOnlyModal ? <th className="text-right">Total Qty</th> : null}
                      {!customerPaidOnlyModal ? (
                        <th
                          className="text-right"
                          style={
                            customerDetailsModal.metric === "amount"
                              ? { background: "#7c3aed", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Amount
                        </th>
                      ) : null}
                      {!customerAmountModal ? (
                        <th
                          className="text-right"
                          style={
                            customerDetailsModal.metric === "paid"
                              ? { background: "#059669", color: "#fff", fontWeight: 800 }
                              : undefined
                          }
                        >
                          Total Paid
                        </th>
                      ) : null}
                      {customerPaidOnlyModal ? <th style={{ width: 230 }}>Actions</th> : null}
                      {!customerPaidOnlyModal ? <th className="text-right">Balance</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {customerDetailsPagedRows.length === 0 ? (
                      <tr>
                        <td colSpan={customerDetailsColSpan} className="empty-state" style={{ padding: "12px" }}>
                          No data for selected customer
                        </td>
                      </tr>
                    ) : (
                      customerDetailsPagedRows.map((row, rowIndex) => (
                        <tr key={row.paymentId || `${row.date}-${rowIndex}`}>
                          <td>
                            {customerPaidOnlyModal && customerPaidEdit.paymentId === row.paymentId ? (
                              <input
                                type="date"
                                className="input-field compact"
                                max={new Date().toISOString().slice(0, 10)}
                                value={customerPaidEdit.date}
                                onChange={(e) => setCustomerPaidEdit((prev) => ({ ...prev, date: e.target.value }))}
                                style={{ width: 160 }}
                                disabled={customerPaidUpdating}
                              />
                            ) : (
                              formatDisplayDate(row.date)
                            )}
                          </td>
                          {customerPaidOnlyModal ? <td>{row.mode || "Cash"}</td> : null}
                          {!customerPaidOnlyModal ? <td className="text-right">{row.entryCount}</td> : null}
                          {!customerPaidOnlyModal ? <td className="text-right">{Number(row.totalQty || 0).toFixed(2)}</td> : null}
                          {!customerPaidOnlyModal ? (
                            <td
                              className="text-right"
                              style={
                                customerDetailsModal.metric === "amount"
                                  ? { background: "#f5f3ff", fontWeight: 700 }
                                  : undefined
                              }
                            >
                              {Number(row.totalAmount || 0).toFixed(2)}
                            </td>
                          ) : null}
                          {!customerAmountModal ? (
                            <td
                              className="text-right"
                              style={
                                customerDetailsModal.metric === "paid"
                                  ? { background: "#ecfdf5", fontWeight: 700 }
                                  : { color: "#16a34a" }
                              }
                            >
                              {customerPaidOnlyModal && customerPaidEdit.paymentId === row.paymentId ? (
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="any"
                                  className="input-field compact numeric"
                                  value={customerPaidEdit.amount}
                                  onChange={(e) => setCustomerPaidEdit((prev) => ({ ...prev, amount: e.target.value }))}
                                  style={{ width: 140, marginLeft: "auto" }}
                                  disabled={customerPaidUpdating}
                                />
                              ) : (
                                Number(row.totalPaid || 0).toFixed(2)
                              )}
                            </td>
                          ) : null}
                          {customerPaidOnlyModal ? (
                            <td>
                              {customerPaidEdit.paymentId === row.paymentId ? (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    type="button"
                                    className="btn-add-payment"
                                    onClick={handleSaveEditCustomerPaid}
                                    disabled={customerPaidUpdating}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    {customerPaidUpdating ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleCancelEditCustomerPaid}
                                    disabled={customerPaidUpdating}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleStartEditCustomerPaid(row)}
                                    disabled={!row.paymentId || customerPaidUpdating || !!customerPaidDeleting[row.paymentId]}
                                    style={{ padding: "0.3rem 0.7rem", fontSize: "0.75rem" }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleDeleteCustomerPaid(row)}
                                    disabled={!row.paymentId || customerPaidUpdating || !!customerPaidDeleting[row.paymentId]}
                                    style={{
                                      padding: "0.3rem 0.7rem",
                                      fontSize: "0.75rem",
                                      borderColor: "#fecaca",
                                      color: "#b91c1c",
                                      background: "#fff5f5",
                                    }}
                                  >
                                    {customerPaidDeleting[row.paymentId] ? "Deleting..." : "Delete"}
                                  </button>
                                </div>
                              )}
                            </td>
                          ) : null}
                          {!customerPaidOnlyModal ? (
                            <td
                              className="text-right"
                              style={{ color: Number(row.balance || 0) > 0 ? "#b91c1c" : "#16a34a" }}
                            >
                              {Number(row.balance || 0).toFixed(2)}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeCustomerDetailsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
